import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getJson } from '@/lib/http';
import { useAuthUser } from '@/hooks/useAuthUser';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Package, Plus, Upload, FileText, CheckCircle, XCircle, AlertCircle, RefreshCw, FileDown, Lock, Unlock, Settings, Play, RotateCcw, ShieldCheck, Smartphone, ChevronLeft, ChevronRight, CheckCircle2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { BriefingRichTextEditor } from '@/components/editor/BriefingRichTextEditor';
import { useToast } from '@/hooks/use-toast';
import { useLocation, useRoute } from 'wouter';
import { useParams } from 'wouter';
// apiRequest import removed - now using snapshot-driven task sending
// removed legacy format dedup
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import PreviewLauncher from '@/pages/pdf-snapshot-preview/PreviewLauncher';
// Removed legacy AdminTaskManagementPanel import
import { OperatorAccordion } from '@/components/signoff/OperatorAccordion';
import { StoreAssignmentCard } from '@/components/signoff/StoreAssignmentCard';
import { StoreReassignedModal, type StoreReassignedResult } from '@/components/signoff/StoreReassignedModal';
import { DistributionConfirmModal } from '@/components/signoff/DistributionConfirmModal';
import { RemovalCoverageGaps, type RemovalGap } from '@/components/signoff/RemovalCoverageGaps';
import { RemovalRunBlockedBanner } from '@/components/signoff/RemovalRunBlockedBanner';
import { RemovalOperatorMismatchBanner } from '@/components/signoff/RemovalOperatorMismatchBanner';
import {
  countAssignmentsByTaskType,
  detectRemovalOperatorMismatches,
  filterAssignmentsByTaskType,
  mismatchesForStoreOperator,
  type TaskTypeFilter,
} from '@shared/task-distribution/removalOperatorMismatch';
import { TransientLoadNotice } from '@/components/TransientLoadNotice';
import { SelectedCycleLockDetails } from '@/components/signoff/SelectedCycleLockDetails';
import type { PostLockWarning } from '@/lib/lockCycleDisplay';
import { invariant } from '@/lib/invariant';
import { buildOperatorTasksFromProcessedRows, sendOperatorTasks } from '@/lib/snapshotTasks';
import {
  AuthenticatedDownloadError,
  downloadAuthenticatedFile,
} from '@/lib/downloadAuthenticated';
import { apiFetch } from '@/lib/api';
import { dbg } from '@/lib/dbg';
import { PDFKIT_SIGNOFF } from '@/lib/flags';
import {
  fetchTaskDistributionBootstrap,
  invalidateTaskDistributionQueries,
  isTaskDistributionBootstrapEnabled,
  taskDistributionQueryKey,
  type TaskDistributionBootstrapPayload,
} from '@/lib/taskDistributionBootstrap';
import {
  parseRemovalRunResolutionEntries,
} from '@/lib/removalRunResolutionMessages';
import {
  getTransientRetryDelayMs,
  isTransientApiError,
  transientQueryShouldRetry,
  transientReadRetryOptions,
} from '@/lib/transientApiErrors';
import { getSignoffV2Pdf } from '@/api/pdfs';
// import { getLockedSummary, type LockedSummary } from '../api/lockedSummary';

// Flat snapshot row used across the Sign-off UI
type SnapRow = {
  lockedAssignmentId: string | number;
  storeId: string | number;
  storeName: string;
  operatorId: string | number | null;
  operatorName: string;
  formatId: string | number | null;
  formatName: string | null;
  campaignId: string | number | null;
  campaignName: string | null;
  expectedQuantity: number;
  taskType?: 'POSTING' | 'REMOVAL';
  removalType?: 'planned' | 'confirmed' | 'possible' | null;
  removalSourceType?: 'planned_delivery' | 'confirmed_delivery' | 'left_with_manager' | string | null;
};

type TableSnapRow = SnapRow & Record<string, unknown>;

type SignoffPdfData = {
  assignments?: TableSnapRow[];
  data?: {
    assignments?: TableSnapRow[];
    removalGaps?: RemovalGap[];
  };
};

interface Assignment {
  id: number; // The assignment ID from locked cycle snapshot
  assignmentId?: number; // Legacy property, may not exist
  campaignId: number;
  storeId: number;
  taskId: number;
  operatorId: number;
  packId: number;
  campaignName: string;
  storeName: string;
  storeAddress: string;
  taskName: string;
  operatorName: string;
  packName: string;
  status: string;
  targetQuantity: number;
  retailerName: string;
  retailerId: number;
  // NEW: Pre-built display-ready format data from cycle snapshot
  finalFormats?: Array<Array<{
    formatName: string;
    quantity: number;
    type: string;
  }>>;
  isFlexible?: boolean;
  // NEW: Format-specific fields from granular locking
  formatId?: number;
  formatName?: string;
  formatQuantity?: number;
  // NEW: Nested assignment structure for frontend compatibility
  assignment?: {
    id: number;
    store: { id: number; name: string };
    pack: { id: number; name: string };
    task: { id: number; name: string };
    campaign: { id: number; name: string };
    operator: { id: number; name: string };
  };
  // Legacy properties - deprecated but may exist
  mediaRequirements?: any[];
  acceptableFormatGroups?: Array<Array<{
    formatId: number;
    quantity: number;
    name?: string;
  }>>;
  selectedFormatGroupIndex?: number; // Track which group was selected by operator
  removedAfterLock?: boolean; // Flag for campaigns deleted after locking
  postingEvents?: Array<{
    id: number;
    eventType: string;
    notes: string;
    mediaFormatQuantities: Record<string, number>;
    createdAt: string;
    evidenceAssets: Array<{
      assetUrl: string;
      assetType: string;
    }>;
  }>;
}

interface LockedCycle {
  id: number;
  cycleId: number;
  data: any; // JSONB data with frozen assignment snapshots
  lockedAt: string;
  lockedBy: number | null;
  lockedByName: string | null;
  lockedByEmail?: string | null;
  lockedByDisplay?: string | null;
  lockReason?: string;
  version: number;
  reassignmentId?: number | null;
  removalGenerationMode?: 'planned_only' | 'confirmed_plus_planned' | null;
  postLockWarnings?: PostLockWarning[];
}

interface MediaFormat {
  id: number;
  name: string;
  description: string;
  type: string;
  quantity: number;
  quantityType: string;
}

interface Retailer {
  id: number;
  name: string;
  description: string;
}

interface BookingCycle {
  id: number;
  retailerId: number;
  name: string;
  description: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  locked_flag: boolean;
}

interface Operator {
  id: number;
  username: string;
  email: string;
  role: string;
  isActive: boolean;
  name: string;
}



// Format resolution is now handled via direct fetch calls in useEffect to avoid React hooks order violations

import { AssignmentTable, type ApiAssignment } from '@/components/AssignmentTable';

interface SignOffV2Props {
  printMode?: boolean;
  operatorId?: string;
  cycleId?: string;
  allStores?: boolean;
  retailerId?: string;
}



interface BriefingSection {
  title: string;
  body: string;
}

function hasContent(html: string | undefined): boolean {
  if (!html) return false;
  const text = html.replace(/<[^>]*>/g, '').trim();
  return text.length > 0;
}

function countBriefingSections(edits: Record<string, string>): number {
  return [
    edits.customMessage,
    edits.installationStandards,
    edits.removalStandards,
    edits.conductInStore,
    edits.submissionRequirements,
  ].filter(hasContent).length;
}

function formatBriefingTimestamp(
  value: string | null | undefined,
  fallback = 'Not yet versioned for this cycle',
): string {
  if (!value) return fallback;
  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

type BriefingAckRowShape = {
  operatorId: number;
  operatorName: string;
  status: 'CURRENT' | 'OUTDATED' | 'STALE_OFFLINE' | 'PENDING';
  acknowledgedAt: string | null;
  briefingUpdatedAt: string | null;
  devicePlatform: string | null;
  deviceModel: string | null;
  deviceOsVersion: string | null;
  appVersion: string | null;
  isStaleVersion: boolean;
};

function BriefingAcknowledgementsCard({
  data,
}: {
  data: {
    totalAssigned: number;
    acknowledgedCurrent: number;
    rows: BriefingAckRowShape[];
  };
}) {
  const { rows, totalAssigned, acknowledgedCurrent } = data;

  function statusBadge(status: BriefingAckRowShape['status']) {
    const map: Record<BriefingAckRowShape['status'], { label: string; className: string; title?: string }> = {
      CURRENT: { label: 'Current', className: 'bg-green-100 text-green-800 border-green-200' },
      OUTDATED: {
        label: 'Outdated',
        className: 'bg-amber-100 text-amber-800 border-amber-200',
        title: 'Operator acknowledged an earlier version. They will be re-prompted on next cycle start.',
      },
      STALE_OFFLINE: {
        label: 'Acked offline (stale)',
        className: 'bg-orange-100 text-orange-800 border-orange-200',
        title: 'Operator acknowledged a cached version that was already outdated. A fresh ack will be collected on next cycle start.',
      },
      PENDING: { label: 'Pending', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    };
    const entry = map[status];
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${entry.className}`}
        title={entry.title}
      >
        {entry.label}
      </span>
    );
  }

  function formatAckAt(iso: string | null): string {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }

  function formatDevice(row: BriefingAckRowShape): string {
    const parts = [row.deviceModel, row.deviceOsVersion].filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
  }

  return (
    <CollapsibleCard
      storageKey="sign-off.briefing-acknowledgements"
      title="Briefing Acknowledgements"
      description={
        totalAssigned === 0
          ? 'No operators assigned to this cycle yet'
          : `${acknowledgedCurrent} of ${totalAssigned} operators on the current version`
      }
    >
      <>
        {totalAssigned === 0 ? (
          <p className="text-sm text-muted-foreground">
            Acknowledgements will appear here once operators are assigned to this cycle and
            start opening the briefing on mobile.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                  <th className="py-2 pr-4 font-medium">Operator</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Acknowledged</th>
                  <th className="py-2 pr-4 font-medium">Device</th>
                  <th className="py-2 pr-4 font-medium">App version</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.operatorId} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{row.operatorName}</td>
                    <td className="py-2 pr-4">{statusBadge(row.status)}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{formatAckAt(row.acknowledgedAt)}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{formatDevice(row)}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{row.appVersion ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    </CollapsibleCard>
  );
}

function BriefingMobilePreview({ edits }: { edits: Record<string, string> }) {
  const sections = useMemo<BriefingSection[]>(() => {
    const all: BriefingSection[] = [];
    if (hasContent(edits.customMessage))
      all.push({ title: 'Important Notice', body: edits.customMessage });
    if (hasContent(edits.installationStandards))
      all.push({ title: 'Installation Standards', body: edits.installationStandards });
    if (hasContent(edits.removalStandards))
      all.push({ title: 'Removal Standards', body: edits.removalStandards });
    if (hasContent(edits.conductInStore))
      all.push({ title: 'Conduct in Store', body: edits.conductInStore });
    if (hasContent(edits.submissionRequirements))
      all.push({ title: 'Submission Requirements', body: edits.submissionRequirements });
    return all;
  }, [edits]);

  const [step, setStep] = useState(0);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const allDone = step >= sections.length;
  const current = sections[step];
  const isLast = step === sections.length - 1;

  useEffect(() => {
    setStep(0);
    setScrolledToBottom(false);
  }, [sections.length]);

  useEffect(() => {
    setScrolledToBottom(false);
    const el = scrollRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      if (el.scrollHeight <= el.clientHeight + 8) setScrolledToBottom(true);
    }, 80);
    return () => clearTimeout(timer);
  }, [step, sections]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 40) setScrolledToBottom(true);
  };

  const handleConfirm = () => {
    if (!current) return;
    setScrolledToBottom(false);
    setStep(s => s + 1);
    setTimeout(() => { scrollRef.current?.scrollTo({ top: 0 }); }, 50);
  };

  const handleReset = () => {
    setStep(0);
    setScrolledToBottom(false);
    setTimeout(() => { scrollRef.current?.scrollTo({ top: 0 }); }, 50);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center justify-between w-full px-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Operator Preview
        </p>
        <button
          onClick={handleReset}
          className="text-xs text-muted-foreground hover:text-gray-700 flex items-center gap-1"
          title="Restart preview"
        >
          <RotateCcw className="w-3 h-3" />
          Restart
        </button>
      </div>

      <div className="bg-gray-900 rounded-[36px] p-2.5 shadow-2xl w-[272px] shrink-0">
        <div className="bg-black rounded-[28px] overflow-hidden" style={{ height: 560 }}>
          {/* Status bar */}
          <div className="bg-gray-900 flex justify-between items-center px-5 py-1.5 text-white text-[10px]">
            <span className="font-semibold">9:41</span>
            <span className="opacity-60">{'\u25CF\u25CF\u25CF'} WiFi {'\u25A0'}</span>
          </div>

          <div className="bg-gray-50 flex flex-col" style={{ height: 'calc(100% - 28px)' }}>
            {/* App header */}
            <div
              className="px-3 py-2.5 flex items-center gap-2 shrink-0"
              style={{ backgroundColor: '#0F3A3F' }}
            >
              <ChevronLeft className="text-white w-4 h-4 shrink-0" />
              <span className="text-white font-semibold text-xs flex-1 truncate">
                {allDone ? 'Route Overview' : 'Operator Briefing'}
              </span>
              {!allDone && current && (
                <span className="text-white/60 text-[10px] shrink-0">
                  {step + 1}/{sections.length}
                </span>
              )}
            </div>

            {sections.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-2 p-4 text-center">
                <Info className="w-8 h-8 text-gray-300" />
                <p className="text-gray-400 text-xs">No briefing content yet.</p>
              </div>
            ) : allDone ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-xs">All briefings confirmed</p>
                  <p className="text-gray-400 text-[10px] mt-0.5">You can now begin your route</p>
                </div>
                <button
                  onClick={handleReset}
                  className="mt-1 w-full py-2.5 rounded-xl text-white font-semibold text-xs"
                  style={{ backgroundColor: '#0F3A3F' }}
                >
                  Start Route
                </button>
              </div>
            ) : (
              <>
                {/* Progress dots */}
                <div className="flex justify-center gap-1.5 py-2 shrink-0">
                  {sections.map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        'rounded-full transition-all duration-200',
                        i < step  ? 'w-2 h-2 bg-green-500' :
                        i === step ? 'w-3.5 h-2 bg-gray-700' :
                                     'w-2 h-2 bg-gray-300'
                      )}
                    />
                  ))}
                </div>

                {/* Scrollable content */}
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-3 pb-2 min-h-0"
                  onScroll={handleScroll}
                >
                  <h2 className="font-bold text-gray-900 text-xs mb-2 leading-snug">
                    {current.title}
                  </h2>
                  <div
                    className="prose prose-xs max-w-none text-gray-700 text-[11px] leading-relaxed [&_h2]:text-xs [&_h3]:text-[11px] [&_ul]:pl-4 [&_ol]:pl-4 [&_li]:text-[11px] [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded [&_iframe]:w-full [&_iframe]:rounded [&_iframe]:aspect-video"
                    dangerouslySetInnerHTML={{ __html: current.body }}
                  />
                  <div className="h-3" />
                </div>

                {/* Scroll hint */}
                {!scrolledToBottom && (
                  <div className="px-3 pb-1 shrink-0">
                    <p className="text-center text-gray-400 text-[10px] flex items-center justify-center gap-1">
                      <ChevronRight className="w-2.5 h-2.5 rotate-90" />
                      Scroll to read
                    </p>
                  </div>
                )}

                {/* Confirm button */}
                <div className="px-3 pb-3 pt-1 shrink-0">
                  <button
                    disabled={!scrolledToBottom}
                    onClick={handleConfirm}
                    className={cn(
                      'w-full py-2.5 rounded-xl font-semibold text-xs transition-all duration-200',
                      scrolledToBottom
                        ? 'text-white shadow-md active:scale-95'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    )}
                    style={scrolledToBottom ? { backgroundColor: '#0F3A3F' } : {}}
                  >
                    {isLast ? 'I have read and understood this' : 'Confirm & Continue'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="w-16 h-1 bg-gray-400 rounded-full" />
    </div>
  );
}

export default function SignOffV2({ printMode = false, operatorId, cycleId, allStores = true, retailerId }: SignOffV2Props) {
  dbg.log('SignOffV2 initialized with props:', { printMode, operatorId, cycleId, allStores, retailerId });

  const [selectedRetailer, setSelectedRetailer] = useState<string>(retailerId || '');
  const [selectedYear, setSelectedYear] = useState<string>(() => new Date().getFullYear().toString());
  const [selectedCycle, setSelectedCycle] = useState<string>(cycleId || '');
  const [selectedOperator, setSelectedOperator] = useState<string>(operatorId || 'all');
  const [selectedStore, setSelectedStore] = useState<string>(allStores ? 'all' : '');
  // Neutral filters + dev bypass
  const [search, setSearch] = useState<string>('');
  const [formatFilter, setFormatFilter] = useState<string>('ALL');
  const [includeUnassignedCampaigns, setIncludeUnassignedCampaigns] = useState<boolean>(true);
  const [taskTypeFilter, setTaskTypeFilter] = useState<TaskTypeFilter>('all');
  const nofilter = new URLSearchParams(window.location.search).get('nofilter') === '1';
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const yearOptions = useMemo(() => {
    const baseYear = new Date().getFullYear();
    return [baseYear - 1, baseYear, baseYear + 1];
  }, []);
  const [previewOperator, setPreviewOperator] = useState<{
    operatorId: string;
    operatorName: string;
    assignments: ApiAssignment[];
  } | null>(null);



  // Add tracing utility function for operator renders
  const traceRender = useRef((component: string, id: string | number) => {
    dbg.log(`TRACE_RENDER: ${component}`, { id });
  });
  const [location] = useLocation();
  const [match, params] = useRoute('/sign-off-v2/:cycleId');
  const routeParams = useParams<{ cycleId?: string }>();
  const locationSearch: string = (location as any)?.search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const searchParams = new URLSearchParams(locationSearch || '');
  const selectedCycleId = selectedCycle || '';
  const cycleIdFromParam = params?.cycleId;
  const resolvedCycleId = (cycleId ?? routeParams?.cycleId ?? searchParams.get('cycleId') ?? selectedCycleId) || null;
  const isDeepLinkedCycle = !!(cycleId ?? routeParams?.cycleId ?? searchParams.get('cycleId'));
  const useTaskDistributionBootstrap = isTaskDistributionBootstrapEnabled();
  // When bootstrap is on and a cycle is resolved, skip legacy fan-out entirely —
  // gate on intent, not on bootstrap.data (which is undefined while in flight).
  const skipLegacyFanOutForCycle = useTaskDistributionBootstrap && !!resolvedCycleId;
  dbg.log('[SIGN-OFF] using cycleId:', resolvedCycleId, 'bootstrap:', useTaskDistributionBootstrap);

  // Deep link (/sign-off-v2/:cycleId): resolve retailer + year so filters populate
  // without requiring the user to re-pick them. One lightweight lookup — only when
  // the URL names a cycle but retailer state is still empty.
  const { data: deepLinkCycle } = useQuery<BookingCycle | null>({
    queryKey: ['/api/booking-cycles', 'deep-link', resolvedCycleId],
    queryFn: async () => {
      const cycles = await getJson<BookingCycle[]>('/api/booking-cycles');
      return cycles.find((c) => c.id.toString() === resolvedCycleId) ?? null;
    },
    enabled: !!resolvedCycleId && !selectedRetailer && !printMode && isDeepLinkedCycle,
    staleTime: 5 * 60 * 1000,
    ...transientReadRetryOptions,
  });

  useEffect(() => {
    if (!deepLinkCycle || selectedRetailer) return;
    setSelectedRetailer(String(deepLinkCycle.retailerId));
    const startYear = deepLinkCycle.startDate
      ? new Date(deepLinkCycle.startDate).getFullYear()
      : null;
    const endYear = deepLinkCycle.endDate
      ? new Date(deepLinkCycle.endDate).getFullYear()
      : null;
    const cycleYear = startYear ?? endYear;
    if (cycleYear) setSelectedYear(String(cycleYear));
  }, [deepLinkCycle, selectedRetailer]);

  // MUST COME BEFORE any useMemo that reads lockedCycle
  const {
    data: taskDistributionBootstrap,
    isLoading: isLoadingBootstrap,
    error: bootstrapError,
    isSuccess: bootstrapSuccess,
    isFetching: isBootstrapFetching,
    failureCount: bootstrapFailureCount,
    refetch: refetchBootstrap,
  } = useQuery<TaskDistributionBootstrapPayload>({
    queryKey: taskDistributionQueryKey(resolvedCycleId),
    queryFn: () => fetchTaskDistributionBootstrap(resolvedCycleId!),
    enabled: !!resolvedCycleId && useTaskDistributionBootstrap,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.status === 404) return false;
      return transientQueryShouldRetry(failureCount, error);
    },
    retryDelay: getTransientRetryDelayMs,
  });

  // Check if cycle is locked and load its snapshot (legacy fan-out path)
  const {
    data: lockedCycleLegacy,
    isLoading: isLoadingLockedLegacy,
    error: lockedCycleErrorLegacy,
    failureCount: lockedLegacyFailureCount,
    isFetching: isLockedLegacyFetching,
  } = useQuery<LockedCycle>({
    queryKey: ['/api/locked-cycles', resolvedCycleId],
    queryFn: async () => {
      console.debug('[QUERY_DEBUG] Fetching /api/locked-cycles/:id', resolvedCycleId);
      const data = await getJson<LockedCycle>(`/api/locked-cycles/${resolvedCycleId}`);
      console.debug('[QUERY_DEBUG] /api/locked-cycles response:', data);
      return data;
    },
    enabled: !!resolvedCycleId && !useTaskDistributionBootstrap,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if ((error as any)?.status === 404) return false;
      return transientQueryShouldRetry(failureCount, error);
    },
    retryDelay: getTransientRetryDelayMs,
  });

  const lockedCycleFromBootstrap = useMemo((): LockedCycle | undefined => {
    if (!taskDistributionBootstrap) return undefined;
    const { meta, lock } = taskDistributionBootstrap;
    return {
      id: lock.id,
      cycleId: meta.cycleId,
      // Intentionally empty — bootstrap does not ship the lock blob; nothing on this page reads .data today.
      data: {},
      lockedAt: meta.lockedAt ?? '',
      lockedBy: meta.lockedBy,
      lockedByName: meta.lockedByName,
      version: lock.version,
      removalGenerationMode: meta.removalGenerationMode as LockedCycle['removalGenerationMode'],
      postLockWarnings: lock.postLockWarnings,
    };
  }, [taskDistributionBootstrap]);

  const lockedCycle = useTaskDistributionBootstrap ? lockedCycleFromBootstrap : lockedCycleLegacy;
  const isLoadingLocked = useTaskDistributionBootstrap ? isLoadingBootstrap : isLoadingLockedLegacy;
  const lockedCycleError = useTaskDistributionBootstrap ? bootstrapError : lockedCycleErrorLegacy;

  // Unified PDF data: assignments-only source of truth (legacy fan-out path)
  const {
    data: unifiedPdfDataLegacy,
    isError: pdfDataErrorLegacy,
    error: signoffDataErrorLegacy,
    isSuccess: pdfDataSuccessLegacy,
    dataUpdatedAt: pdfDataUpdatedAtLegacy,
    isFetching: isPdfDataFetchingLegacy,
    failureCount: signoffLegacyFailureCount,
  } = useQuery<SignoffPdfData | null>({
    queryKey: ['/api/locked-cycles', resolvedCycleId, 'signoff-v2-data'],
    queryFn: async () => {
      if (!resolvedCycleId) return null;
      const res = await apiFetch(`/api/locked-cycles/${resolvedCycleId}/signoff-v2-data`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      let body: Record<string, unknown> = {};
      try {
        body = await res.json();
      } catch {
        body = {};
      }
      if (res.status === 404) return null;
      if (res.status === 409) {
        const data = body.data as SignoffPdfData['data'] | undefined;
        return {
          assignments: Array.isArray(data?.assignments) ? data.assignments : [],
          data: {
            assignments: Array.isArray(data?.assignments) ? data.assignments : [],
            removalGaps: data?.removalGaps ?? [],
          },
        };
      }
      if (!res.ok) {
        const detail = typeof body.error === 'string'
          ? body.error
          : typeof body.message === 'string'
            ? body.message
            : `HTTP ${res.status}`;
        throw new Error(detail);
      }
      return body as SignoffPdfData;
    },
    enabled: !!resolvedCycleId && !useTaskDistributionBootstrap,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => transientQueryShouldRetry(failureCount, error),
    retryDelay: getTransientRetryDelayMs,
  });

  const unifiedPdfData = useMemo((): SignoffPdfData | null | undefined => {
    if (useTaskDistributionBootstrap) {
      if (!taskDistributionBootstrap) return undefined;
      return {
        assignments: taskDistributionBootstrap.assignments as TableSnapRow[],
        data: {
          assignments: taskDistributionBootstrap.assignments as TableSnapRow[],
          removalGaps: taskDistributionBootstrap.removalGaps,
        },
      };
    }
    return unifiedPdfDataLegacy;
  }, [useTaskDistributionBootstrap, taskDistributionBootstrap, unifiedPdfDataLegacy]);

  const pdfDataError = useTaskDistributionBootstrap ? !!bootstrapError && !taskDistributionBootstrap : pdfDataErrorLegacy;
  const pdfDataSuccess = useTaskDistributionBootstrap ? bootstrapSuccess : pdfDataSuccessLegacy;
  const pdfDataUpdatedAt = useTaskDistributionBootstrap
    ? (taskDistributionBootstrap?.meta?.dataAsOf
        ? new Date(taskDistributionBootstrap.meta.dataAsOf).getTime()
        : undefined)
    : pdfDataUpdatedAtLegacy;
  const isPdfDataFetching = useTaskDistributionBootstrap ? isBootstrapFetching : isPdfDataFetchingLegacy;
  const removalRunBlocked = useTaskDistributionBootstrap && !!taskDistributionBootstrap?.removalBlocked;
  const removalRunResolutionFailures = useMemo(
    () => parseRemovalRunResolutionEntries(taskDistributionBootstrap?.removalRunResolution).filter((entry) => !entry.ok),
    [taskDistributionBootstrap?.removalRunResolution],
  );
  const removalRunCycleNameById = useMemo(() => {
    const map = new Map<number, string>();
    if (taskDistributionBootstrap?.meta?.cycleId != null && taskDistributionBootstrap.meta.cycleName) {
      map.set(taskDistributionBootstrap.meta.cycleId, taskDistributionBootstrap.meta.cycleName);
    }
    for (const cycle of taskDistributionBootstrap?.filters?.bookingCycles ?? []) {
      map.set(cycle.id, cycle.name);
    }
    return map;
  }, [taskDistributionBootstrap?.meta, taskDistributionBootstrap?.filters?.bookingCycles]);

  const refreshDistributionData = useCallback(() => {
    if (!resolvedCycleId) return;
    invalidateTaskDistributionQueries(queryClient, resolvedCycleId);
  }, [queryClient, resolvedCycleId]);

  const retryAssignmentLoad = useCallback(() => {
    if (!resolvedCycleId) return;
    if (useTaskDistributionBootstrap) {
      void refetchBootstrap();
      return;
    }
    refreshDistributionData();
  }, [refetchBootstrap, refreshDistributionData, resolvedCycleId, useTaskDistributionBootstrap]);

  // Show toast only once per cycle when data is successfully loaded but empty
  const lastEmptyToastCycleRef = useRef<string | null>(null);
  useEffect(() => {
    // Handle both response structures:
    // 1. { assignments: [...] } - assignments at top level
    // 2. { data: { assignments: [...] } } - assignments nested inside data
    const assignments = unifiedPdfData?.assignments ?? unifiedPdfData?.data?.assignments;
    if (pdfDataSuccess &&
      resolvedCycleId &&
      unifiedPdfData &&
      (!assignments || assignments.length === 0) &&
      lastEmptyToastCycleRef.current !== resolvedCycleId) {
      lastEmptyToastCycleRef.current = resolvedCycleId;
      toast({
        title: 'No Assignments Found',
        description: 'This cycle has no assignments. Ensure campaigns are confirmed and have valid snapshots.',
        variant: 'destructive',
      });
    }
  }, [pdfDataSuccess, unifiedPdfData, resolvedCycleId, toast]);

  // Handle 404 for locked cycle gracefully: keep selection and mark unlocked
  const isLockedNotFound = !!lockedCycleError && (
    (lockedCycleError as any)?.status === 404 || (lockedCycleError as any)?.message?.includes('404')
  );
  const effectiveLockedCycle = isLockedNotFound ? null : lockedCycle;

  // ─── Operator Briefing (auto-populates from retailer standards) ───
  const briefingQueryKey = ['cycle-briefing', resolvedCycleId];
  const { data: briefingData, isLoading: briefingLoading } = useQuery<{
    briefing: {
      id: number | null;
      cycleId: number;
      retailerId: number;
      sourceStandardsVersionId: number | null;
      installationStandards: string;
      removalStandards: string;
      conductInStore: string;
      submissionRequirements: string;
      customMessage: string;
      createdAt: string | null;
      updatedAt: string | null;
    };
    source: 'cycle' | 'standards_default';
    newerStandardsAvailable?: {
      versionId: number;
      installationStandards: string;
      removalStandards: string;
      conductInStore: string;
      submissionRequirements: string;
    };
  }>({
    queryKey: briefingQueryKey,
    queryFn: () => getJson(`/api/retailers/cycle-briefing/${resolvedCycleId}`),
    enabled: !!resolvedCycleId,
    staleTime: 60_000,
    ...transientReadRetryOptions,
  });

  const [briefingEdits, setBriefingEdits] = useState<Record<string, string>>({});
  const [briefingDirty, setBriefingDirty] = useState(false);
  const [refreshedStandardsVersionId, setRefreshedStandardsVersionId] = useState<number | null>(null);
  const briefingSectionCount = useMemo(() => countBriefingSections(briefingEdits), [briefingEdits]);
  const savedBriefingSectionCount = useMemo(() => {
    if (briefingData?.source !== 'cycle' || !briefingData.briefing) return 0;

    return countBriefingSections({
      installationStandards: briefingData.briefing.installationStandards,
      removalStandards: briefingData.briefing.removalStandards,
      conductInStore: briefingData.briefing.conductInStore,
      submissionRequirements: briefingData.briefing.submissionRequirements,
      customMessage: briefingData.briefing.customMessage,
    });
  }, [briefingData]);
  const briefingAvailableForMobileSend = savedBriefingSectionCount > 0;

  useEffect(() => {
    if (briefingData?.briefing) {
      setBriefingEdits({
        installationStandards: briefingData.briefing.installationStandards,
        removalStandards: briefingData.briefing.removalStandards,
        conductInStore: briefingData.briefing.conductInStore,
        submissionRequirements: briefingData.briefing.submissionRequirements,
        customMessage: briefingData.briefing.customMessage,
      });
      // Enable Save immediately when briefing hasn't been saved for this cycle yet
      setBriefingDirty(briefingData.source === 'standards_default');
      setRefreshedStandardsVersionId(null);
    }
  }, [briefingData]);

  const briefingSaveMutation = useMutation({
    mutationFn: async (body: { content: Record<string, string> }) => {
      const { content } = body;
      const res = await apiFetch(`/api/retailers/cycle-briefing/${resolvedCycleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...content,
          significantChange: true,
          sourceStandardsVersionId: refreshedStandardsVersionId ?? briefingData?.briefing?.sourceStandardsVersionId ?? null,
        }),
      });
      if (!res.ok) throw new Error('Failed to save briefing');
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Briefing saved' });
      queryClient.invalidateQueries({ queryKey: briefingQueryKey });
      queryClient.invalidateQueries({ queryKey: ['cycle-briefing-acknowledgements', resolvedCycleId] });
      setBriefingDirty(false);
      setRefreshedStandardsVersionId(null);
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  function handleBriefingFieldChange(field: string, value: string) {
    setBriefingEdits(prev => ({ ...prev, [field]: value }));
    setBriefingDirty(true);
  }

  function handleRefreshFromStandards() {
    if (!briefingData?.newerStandardsAvailable) return;
    const newer = briefingData.newerStandardsAvailable;
    setBriefingEdits(prev => ({
      ...prev,
      installationStandards: newer.installationStandards,
      removalStandards: newer.removalStandards,
      conductInStore: newer.conductInStore,
      submissionRequirements: newer.submissionRequirements,
    }));
    setRefreshedStandardsVersionId(newer.versionId);
    setBriefingDirty(true);
  }

  // ─── Acknowledgements (admin audit) ───
  type BriefingAckRow = {
    operatorId: number;
    operatorName: string;
    status: 'CURRENT' | 'OUTDATED' | 'STALE_OFFLINE' | 'PENDING';
    acknowledgedAt: string | null;
    briefingUpdatedAt: string | null;
    devicePlatform: string | null;
    deviceModel: string | null;
    deviceOsVersion: string | null;
    appVersion: string | null;
    isStaleVersion: boolean;
  };
  const { data: acksData } = useQuery<{
    briefingUpdatedAt: string | null;
    totalAssigned: number;
    acknowledgedCurrent: number;
    rows: BriefingAckRow[];
  }>({
    queryKey: ['cycle-briefing-acknowledgements', resolvedCycleId],
    queryFn: () => getJson(`/api/retailers/cycle-briefing/${resolvedCycleId}/acknowledgements`),
    enabled: !!resolvedCycleId,
    staleTime: 30_000,
    ...transientReadRetryOptions,
  });
  const briefingSavedAtLabel = formatBriefingTimestamp(
    briefingData?.source === 'cycle' ? briefingData?.briefing?.updatedAt : null,
    briefingData?.source === 'standards_default'
      ? 'Using retailer standard (not yet saved for this cycle)'
      : 'Not yet versioned for this cycle',
  );

  // Guard: if no locked data, render empty state immediately to avoid heavy compute
  // Show empty state ONLY when a cycle is selected/requested but no data is available
  // Note: Do NOT early-return here; allow the UI to render selectors even when there's no data yet.

  // Sync selectedCycle state with resolvedCycleId so dropdown and queries align
  // Remove auto-selection to require manual selection by user

  // Fetch hardened snapshot data if using new format
  const isLoadingHardened = false;

  // Extract rows strictly from unified pdf-data (assignments[] only)
  const rows = useMemo(() => {
    const pdfData = unifiedPdfData; // TODO: wire to query below
    // Handle both response structures:
    // 1. { assignments: [...] } - assignments at top level
    // 2. { data: { assignments: [...] } } - assignments nested inside data
    const assignments = pdfData?.assignments ?? pdfData?.data?.assignments;
    if (!assignments) {
      dbg.log('[SIGN-OFF] No pdf-data assignments available');
      return [] as any[];
    }
    dbg.log('[SIGN-OFF] Using unified assignments from pdf-data:', assignments.length);
    return assignments.map((a: any) => ({
      // Preserve the backend's removal_obligation_id so removals stay distinct
      // through client dedup and React row keys.
      id: a.realAssignmentId != null
        ? `real-${a.realAssignmentId}`
        : `${a.operatorId}-${a.storeId}-${a.campaignId}-${a.packId}-${a.mediaFormatId}`,
      campaignId: a.campaignId,
      campaignName: a.campaignName || 'Unknown Campaign',
      packId: a.packId,
      packName: a.packName,
      taskId: a.taskId,
      taskName: a.taskName,
      storeId: a.storeId,
      storeName: a.storeName || 'Unknown Store',
      operatorId: a.operatorId,
      operatorName: a.operatorName || 'Unknown Operator',
      mediaFormatId: a.mediaFormatId,
      mediaFormatName: a.mediaFormatName || 'Unknown Format',
      quantity: a.quantity,
      // compatibility
      formatId: a.mediaFormatId,
      formatName: a.mediaFormatName,
      expectedQuantity: a.quantity,
      realAssignmentId: a.realAssignmentId,
      legacyAssignmentId: a.legacyAssignmentId,
      taskType: a.taskType,
      removalType: a.removalType,
      removalSourceType: a.removalSourceType,
      mediaFormat: { id: a.mediaFormatId, name: a.mediaFormatName },
      campaign: { id: a.campaignId, name: a.campaignName },
      task: { id: a.taskId, name: a.taskName },
      pack: { id: a.packId, name: a.packName },
    }));
  }, [unifiedPdfData, resolvedCycleId]);

  const taskTypeCounts = useMemo(() => {
    const { postingCount, removalCount } = countAssignmentsByTaskType(
      rows as Parameters<typeof countAssignmentsByTaskType>[0],
    );
    return { all: rows.length, postingCount, removalCount };
  }, [rows]);

  const taskTypeFilteredRows = useMemo(
    () => filterAssignmentsByTaskType(rows as Parameters<typeof filterAssignmentsByTaskType>[0], taskTypeFilter),
    [rows, taskTypeFilter],
  );

  // Count operators for logging as requested
  const operatorsCount = useMemo(() => {
    if (!rows || rows.length === 0) return 0;
    const uniqueOperators = new Set(rows.map((r: any) => r.operatorId ?? r.operator_id));
    return uniqueOperators.size;
  }, [rows]);

  // Console log once when component mounts with cycle and operators data
  useEffect(() => {
    if (cycleId && operatorsCount > 0) {
      console.log('SIGNOFF_V2_MOUNT', { cycleId, operatorsCount });
    }
  }, [cycleId, operatorsCount]);

  // Set loading states
  const assignmentLoadError = useTaskDistributionBootstrap
    ? bootstrapError
    : (lockedCycleErrorLegacy ?? signoffDataErrorLegacy ?? null);
  const isTransientAssignmentFailure =
    assignmentLoadError != null && isTransientApiError(assignmentLoadError);
  const isTransientAssignmentRetry = useTaskDistributionBootstrap
    ? isBootstrapFetching && bootstrapFailureCount > 0
    : (isLockedLegacyFetching || isPdfDataFetchingLegacy)
      && (lockedLegacyFailureCount > 0 || signoffLegacyFailureCount > 0);
  const isLoadingAssignments = isLoadingLocked || isLoadingHardened || isTransientAssignmentRetry;
  const assignmentError = assignmentLoadError && !isTransientAssignmentFailure ? assignmentLoadError : null;
  const meta = useMemo(() => ({ source: 'snapshot', totalAssignments: rows.length }), [rows]);

  // Derive stores and flat rows from snapshot rows
  const snapStores = useMemo(() => {
    const m = new Map<any, { id: any; name: string }>();
    for (const r of rows) {
      const id = (r as any)?.storeId ?? null;
      if (id == null) continue;
      if (!m.has(id)) m.set(id, { id, name: (r as any)?.storeName ?? 'Unknown Store' });
    }
    return Array.from(m.values());
  }, [rows]);
  const snapAllRows: TableSnapRow[] = rows as unknown as TableSnapRow[];
  const snapRawRows = snapAllRows.length;
  const snapNoFilter = new URLSearchParams(window.location.search).get('nofilter') === '1';
  function applyFiltersSafely(rows: any[], f: any) {
    try {
      // Minimal safe filters: store/operator selections and unassigned toggle
      let r = rows;
      if (selectedStore !== 'all') r = r.filter(x => `${x.storeId ?? ''}` === selectedStore);
      if (selectedOperator !== 'all') r = r.filter(x => `${x.operatorId ?? ''}` === `${selectedOperator}`);
      if (!includeUnassignedCampaigns) r = r.filter(x => (x.campaignName ?? 'Unassigned') !== 'Unassigned');
      if (taskTypeFilter !== 'all') {
        r = filterAssignmentsByTaskType(r, taskTypeFilter);
      }
      return r;
    } catch { return rows; }
  }
  const snapDisplayedRows = snapNoFilter ? snapAllRows : applyFiltersSafely(snapAllRows, {
    selectedStore, selectedOperator, includeUnassignedCampaigns, taskTypeFilter
  });

  // Media format name mapping for display (same as PDF)
  const mediaFormatMap: Record<number, string> = {
    16: "SV Aisle Fin",
    18: "SV Floor Graphic",
    25: "SV Beer Shelf-Talker",
    38: "SV Fridge Vinyl",
    39: "SV Chiller Door Vinyl",
    40: "SV Freezer Door Vinyl",
    41: "SV Product Highlight Sticker",
    42: "SV Window Cling",
    // Add more as needed
  };

  // Get current user info to check admin status
  const { user: currentUser } = useAuthUser();
  console.log("[AUTH_DEBUG]", "Loaded user in sign-off-v2.tsx", currentUser);

  const canDownloadAdminExports = (() => {
    const role = currentUser?.role ?? '';
    const roles = Array.isArray(currentUser?.roles) ? currentUser.roles : [];
    return (
      role === 'admin'
      || role === 'super_admin'
      || role === 'superadmin'
      || roles.includes('admin')
      || roles.includes('super_admin')
      || roles.includes('superadmin')
    );
  })();

  // Unlock cycle mutation
  const unlockCycleMutation = useMutation({
    mutationFn: async (cycleId: number) => {
      const response = await apiFetch(`/api/cycles/${cycleId}/unlock`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to unlock cycle');
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Cycle Unlocked",
        description: "The cycle has been unlocked successfully. You can now make changes to campaigns and assignments.",
      });

      // Navigate to cycles dashboard for editing
      window.location.href = '/cycles';
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Unlock Cycle",
        description: error.message || "An error occurred while unlocking the cycle.",
        variant: "destructive",
      });
    },
  });

  // In print mode, ensure we use the passed props
  useEffect(() => {
    if (printMode) {
      console.log('SignOffV2 in print mode, setting state from props:', { retailerId, cycleId, operatorId, allStores });
      if (retailerId) setSelectedRetailer(retailerId);
      if (cycleId) setSelectedCycle(cycleId);
      if (operatorId) setSelectedOperator(operatorId);
      setSelectedStore(allStores ? 'all' : '');
    }
  }, [printMode, retailerId, cycleId, operatorId, allStores]);

  // Query for retailers
  const { data: retailersQuery = [] } = useQuery<Retailer[]>({
    queryKey: ['/api/retailers'],
    queryFn: async () => {
      console.debug('[QUERY_DEBUG] Fetching /api/retailers');
      const data = await getJson<Retailer[]>('/api/retailers');
      console.debug('[QUERY_DEBUG] /api/retailers response:', data?.length || 0, 'retailers');
      return Array.isArray(data) ? data : [];
    },
    enabled: !skipLegacyFanOutForCycle,
  });

  const retailers = useMemo(() => {
    if (useTaskDistributionBootstrap && taskDistributionBootstrap?.filters?.retailers?.length) {
      return taskDistributionBootstrap.filters.retailers.map((retailer) => ({
        id: retailer.id,
        name: retailer.name,
        description: '',
      }));
    }
    return retailersQuery;
  }, [useTaskDistributionBootstrap, taskDistributionBootstrap, retailersQuery]);

  // Query for booking cycles (scoped by retailerId) — show all cycles regardless of lock status
  const { data: bookingCyclesQuery = [] } = useQuery<BookingCycle[]>({
    queryKey: ['/api/booking-cycles', selectedRetailer, selectedYear],
    queryFn: async () => {
      if (!selectedRetailer) return [] as BookingCycle[];
      const yearQuery = selectedYear ? `&year=${selectedYear}` : '';
      console.debug('[QUERY_DEBUG] Fetching /api/booking-cycles?retailerId=', selectedRetailer, 'year:', selectedYear);
      const cycles = await getJson<BookingCycle[]>(`/api/booking-cycles?retailerId=${selectedRetailer}${yearQuery}`);
      console.debug('[QUERY_DEBUG] Loaded', (cycles as any[])?.length || 0, 'booking cycles for retailer', selectedRetailer, 'year', selectedYear);
      return Array.isArray(cycles) ? cycles : [];
    },
    enabled: !!selectedRetailer && !skipLegacyFanOutForCycle,
  });

  const bookingCycles = useMemo(() => {
    if (useTaskDistributionBootstrap && taskDistributionBootstrap?.filters?.bookingCycles?.length) {
      return taskDistributionBootstrap.filters.bookingCycles.map((cycle) => ({
        id: cycle.id,
        retailerId: cycle.retailerId,
        name: cycle.name,
        description: '',
        isActive: true,
        startDate: cycle.startDate ?? '',
        endDate: cycle.endDate ?? '',
        locked_flag: cycle.locked_flag,
      }));
    }
    return bookingCyclesQuery;
  }, [useTaskDistributionBootstrap, taskDistributionBootstrap, bookingCyclesQuery]);

  const filteredBookingCycles = useMemo(() => {
    if (!bookingCycles || bookingCycles.length === 0) return [] as BookingCycle[];
    const selectedYearNumber = Number(selectedYear);

    return bookingCycles.filter((cycle) => {
      if (!selectedYear) return true;
      const startYear = cycle.startDate ? new Date(cycle.startDate).getFullYear() : null;
      const endYear = cycle.endDate ? new Date(cycle.endDate).getFullYear() : null;
      const cycleYear = startYear ?? endYear;
      if (!cycleYear) return true; // Keep cycles without date info
      return cycleYear === selectedYearNumber;
    });
  }, [bookingCycles, selectedYear]);

  // Removed: eligible-with-tasks — we list all booking cycles for the retailer

  // Sync selectedCycle with resolvedCycleId when it matches the current year filter
  useEffect(() => {
    if (!resolvedCycleId) return;

    const cycleMatchesYear = filteredBookingCycles.length === 0
      ? true
      : filteredBookingCycles.some((cycle) => cycle.id.toString() === resolvedCycleId);

    if (cycleMatchesYear && selectedCycle !== resolvedCycleId) {
      setSelectedCycle(resolvedCycleId);
    }
  }, [filteredBookingCycles, resolvedCycleId, selectedCycle]);

  // Auto-select the most recent booking cycle for the selected retailer if none selected
  useEffect(() => {
    if (!printMode && selectedRetailer && !selectedCycle && filteredBookingCycles.length > 0) {
      const mostRecent = [...filteredBookingCycles].sort((a, b) => b.id - a.id)[0];
      console.log('Auto-selecting booking cycle:', { retailerId: selectedRetailer, cycleId: mostRecent.id, cycleName: mostRecent.name });
      setSelectedCycle(mostRecent.id.toString());
    }
  }, [filteredBookingCycles, printMode, selectedRetailer, selectedCycle]);

  // Clear selected cycle if it no longer matches the selected year — but not
  // while retailer cycles are still loading (empty list).
  useEffect(() => {
    if (printMode) return;
    if (!selectedCycle) return;

    const cycleMatchesYear = filteredBookingCycles.length === 0
      ? true
      : filteredBookingCycles.some((cycle) => cycle.id.toString() === selectedCycle);
    if (!cycleMatchesYear) {
      setSelectedCycle('');
    }
  }, [filteredBookingCycles, printMode, selectedCycle]);

  // Removed fallback based on eligibleCycles (no longer used)

  // TEMPORARILY COMMENTED OUT - Locked summary query
  // const { data: lockedSummary, isLoading: isLoadingLockedSummary } = useQuery<LockedSummary>({
  //   queryKey: ['locked-summary', selectedCycle, selectedStore !== 'all' ? selectedStore : null],
  //   queryFn: async () => {
  //     if (!selectedCycle || selectedStore === 'all') {
  //       throw new Error('Need specific cycle and store for locked summary');
  //     }
  //     return getLockedSummary(parseInt(selectedCycle), parseInt(selectedStore));
  //     },
  //   enabled: !!selectedCycle && !!lockedCycle && selectedStore !== 'all',
  //   retry: false,
  // });

  // TEMPORARILY COMMENTED OUT - Extract operators from locked summary
  // const operators = useMemo(() => {
  //   if (!lockedSummary) return [];
  //   return lockedSummary.storeOperators.map(op => ({
  //     id: op.id,
  //     name: op.name,
  //     username: op.name // Fallback for existing code
  //     }));
  // }, [lockedSummary]);

  // TEMPORARILY COMMENTED OUT - Fallback operators
  // const fallbackOperators = useMemo(() => {
  //   if (operators.length > 0) return operators;
  //   
  //   // Return empty array to prevent rendering errors
  //   return [];
  // });

  // (moved) operators array is defined after operatorLookup below to avoid TDZ

  const isLoadingLockedSummary = false;
  // Define a safe placeholder for lockedSummary to avoid ReferenceError in render paths
  const lockedSummary: any = null;

  // Query for stores (filtered by retailer)
  const { data: storesQuery = [] } = useQuery<any[]>({
    queryKey: ['/api/stores', selectedRetailer],
    queryFn: async () => {
      console.debug('[QUERY_DEBUG] Fetching /api/stores');
      const allStores = await getJson<any[]>('/api/stores');
      const filtered = selectedRetailer ? allStores.filter((store: any) => store.retailerId === parseInt(selectedRetailer)) : allStores;
      console.debug('[QUERY_DEBUG] /api/stores response:', filtered?.length || 0, 'stores for retailer', selectedRetailer);
      return filtered;
    },
    enabled: !!selectedRetailer && !skipLegacyFanOutForCycle,
  });

  const stores = useTaskDistributionBootstrap && taskDistributionBootstrap?.filters?.stores
    ? taskDistributionBootstrap.filters.stores
    : storesQuery;

  // Query for media formats
  const { data: mediaFormatsQuery = [] } = useQuery<MediaFormat[]>({
    queryKey: ['/api/media-formats'],
    queryFn: async () => {
      console.debug('[QUERY_DEBUG] Fetching /api/media-formats');
      const data = await getJson<MediaFormat[]>('/api/media-formats');
      console.debug('[QUERY_DEBUG] /api/media-formats response:', data?.length || 0, 'formats');
      return Array.isArray(data) ? data : [];
    },
    enabled: !skipLegacyFanOutForCycle,
  });

  const mediaFormats = useTaskDistributionBootstrap && taskDistributionBootstrap?.filters?.mediaFormats
    ? taskDistributionBootstrap.filters.mediaFormats.map((format) => ({
        id: format.id,
        name: format.name,
        description: '',
        type: '',
        quantity: 0,
        quantityType: '',
      }))
    : mediaFormatsQuery;

  // Using assignmentData from the query above
  // Snapshot counts and rows (flat)
  const allRows: TableSnapRow[] = useMemo(() => rows as unknown as TableSnapRow[], [rows]);
  const rawRows = allRows.length;

  // Safely extract assignments from the API response structure
  const unifiedAssignments: any[] = useMemo(() => rows as any[], [rows]);

  // Reset dependent selections when parent changes (but not in print mode)
  useEffect(() => {
    if (!printMode) {
      // Do not clear cycle selection if navigated via /sign-off-v2/:cycleId
      if (!cycleId) {
        setSelectedCycle('');
      }
      setSelectedOperator('all');
      setSelectedStore('all');
    }
  }, [selectedRetailer, printMode, cycleId]);

  useEffect(() => {
    if (!printMode) {
      setSelectedOperator('all');
      setSelectedStore('all');
    }
  }, [selectedCycle, printMode]);

  // Reset store selection when operator changes (but not in print mode)
  useEffect(() => {
    if (!printMode) {
      setSelectedStore('all');
    }
  }, [selectedOperator, printMode]);

  // Get filtered stores
  const filteredStores = useMemo(() => {
    let filtered = stores.filter(store => store.retailerId === parseInt(selectedRetailer));
    if (selectedOperator !== 'all' && rows.length > 0) {
      const operatorStoreIds = new Set(
        rows
          .filter((r: any) => {
            const assignmentOperatorId = r.operatorId ?? r.operator_id;
            const selectedOperatorNum = parseInt(selectedOperator);
            const selectedOperatorStr = selectedOperator.toString();
            return assignmentOperatorId === selectedOperatorNum ||
              assignmentOperatorId === selectedOperatorStr ||
              assignmentOperatorId?.toString() === selectedOperatorStr;
          })
          .map((r: any) => r.storeId ?? r.store_id)
      );
      filtered = filtered.filter(store => operatorStoreIds.has(store.id));
    }
    return filtered;
  }, [stores, selectedRetailer, selectedOperator, rows]);

  // Query for campaigns to get actual campaign names
  const { data: campaignDataQuery = [] } = useQuery<any[]>({
    queryKey: ['/api/campaigns/with-retailers'],
    queryFn: async () => {
      console.debug('[QUERY_DEBUG] Fetching /api/campaigns/with-retailers');
      const data = await getJson<any[]>('/api/campaigns/with-retailers');
      console.debug('[QUERY_DEBUG] /api/campaigns/with-retailers response:', data?.length || 0, 'campaigns');
      return Array.isArray(data) ? data : [];
    },
    enabled: (rows && rows.length > 0) && !useTaskDistributionBootstrap,
  });

  const campaignData = useTaskDistributionBootstrap && taskDistributionBootstrap?.filters?.campaigns
    ? taskDistributionBootstrap.filters.campaigns
    : campaignDataQuery;

  // Unwrap campaigns data defensively (avoid crashes when shape differs)
  const campaigns = useMemo(() => {
    if (Array.isArray(campaignData)) return campaignData;
    // Some endpoints return { data: { campaigns: [...] } }
    const nested = (campaignData as any)?.data?.campaigns;
    if (Array.isArray(nested)) return nested;
    if (campaignData && typeof campaignData === 'object') {
      dbg.warn('[SIGN_OFF_V2] Invalid campaigns data', campaignData);
    }
    return [] as any[];
  }, [campaignData]);


  // Fetch operators data to get real names
  const { data: operatorsDataQuery = [] } = useQuery<any[]>({
    queryKey: ['/api/users/operators'],
    queryFn: async () => {
      console.debug('[QUERY_DEBUG] Fetching /api/users/operators');
      const data = await getJson<any[]>('/api/users/operators');
      console.debug('[QUERY_DEBUG] /api/users/operators response:', data?.length || 0, 'operators');
      return Array.isArray(data) ? data : [];
    },
    enabled: !skipLegacyFanOutForCycle,
  });

  const operatorsData = useTaskDistributionBootstrap && taskDistributionBootstrap?.filters?.operators
    ? taskDistributionBootstrap.filters.operators
    : operatorsDataQuery;

  const { data: mobileAssignmentsSummaryQuery = [] } = useQuery<any[]>({
    queryKey: ['mobile-assignments-summary', resolvedCycleId],
    enabled: !!resolvedCycleId && !useTaskDistributionBootstrap,
    queryFn: async () => {
      if (!resolvedCycleId) return [];
      const result = await getJson(`/api/assignments/mobile-assignments?cycleId=${resolvedCycleId}`);
      return Array.isArray(result) ? result : [];
    },
  });

  const mobileAssignmentsSummary = useTaskDistributionBootstrap && taskDistributionBootstrap?.mobile?.assignmentsSummary
    ? taskDistributionBootstrap.mobile.assignmentsSummary
    : mobileAssignmentsSummaryQuery;

  // Fetch store-level sent status
  const { data: storeSentStatusQuery = [] } = useQuery<any[]>({
    queryKey: ['mobile-assignments-stores', resolvedCycleId],
    enabled: !!resolvedCycleId && !useTaskDistributionBootstrap,
    queryFn: async () => {
      if (!resolvedCycleId) return [];
      const result = await getJson(`/api/assignments/mobile-assignments-stores?cycleId=${resolvedCycleId}`);
      return Array.isArray(result) ? result : [];
    },
  });

  const storeSentStatus = useTaskDistributionBootstrap && taskDistributionBootstrap?.mobile?.storeSendStatus
    ? taskDistributionBootstrap.mobile.storeSendStatus
    : storeSentStatusQuery;

  // Store-level sent lookup: Map<"operatorId-storeId", boolean>
  const storeSentLookup = useMemo(() => {
    const map = new Map<string, boolean>();
    if (Array.isArray(storeSentStatus)) {
      storeSentStatus.forEach((entry) => {
        if (entry?.operatorId != null && entry?.storeId != null) {
          map.set(`${entry.operatorId}-${entry.storeId}`, true);
        }
      });
    }
    return map;
  }, [storeSentStatus]);

  const mobileSentLookup = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(mobileAssignmentsSummary)) {
      mobileAssignmentsSummary.forEach((entry) => {
        if (entry?.operatorId != null && entry?.sentMobileAt) {
          map.set(String(entry.operatorId), entry.sentMobileAt as string);
        }
      });
    }
    return map;
  }, [mobileAssignmentsSummary]);

  const pdfSentLookup = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(mobileAssignmentsSummary)) {
      mobileAssignmentsSummary.forEach((entry) => {
        if (entry?.operatorId != null && entry?.sentPdfAt) {
          map.set(String(entry.operatorId), entry.sentPdfAt as string);
        }
      });
    }
    return map;
  }, [mobileAssignmentsSummary]);

  // Create operator lookup from operators API data for real names
  const operatorLookup = useMemo(() => {
    const operatorMap = new Map();

    console.log('🔍 OPERATOR_LOOKUP: Building lookup with operators data:', operatorsData.length, 'operators');

    // First, populate with real names from operators API
    operatorsData.forEach((operator: any) => {
      if (operator.id && operator.name) {
        console.log('🔍 OPERATOR_LOOKUP: Mapping ID', operator.id, 'to name', operator.name);
        operatorMap.set(operator.id, operator.name);
      }
    });

    // Then, check row data for any operators not in the API (fallback)
    if (rows && rows.length > 0) {
      rows.forEach((r: any) => {
        const operatorId = r.operatorId ?? r.operator_id;
        if (operatorId && !operatorMap.has(operatorId)) {
          // Try to get the best available operator name as fallback
          const betterName = (r as any)?.assignment?.operator?.name || r.operatorName || 'Unknown Operator';
          console.log('🔍 OPERATOR_LOOKUP: Fallback mapping ID', operatorId, 'to name', betterName);
          operatorMap.set(operatorId, betterName);
        }
      });
    }

    console.log('🔍 OPERATOR_LOOKUP: Final lookup map size:', operatorMap.size);
    return operatorMap;
  }, [operatorsData, rows]);

  const openOperatorPreview = (operatorId: string, operatorName: string, groups: any[]) => {
    const assignments: ApiAssignment[] = groups.flatMap((group: any) => {
      const groupAssignments = Array.isArray(group.assignments) ? group.assignments : [];
      return groupAssignments as ApiAssignment[];
    });

    setPreviewOperator({
      operatorId,
      operatorName,
      assignments,
    });
  };

  // Create operators array from the lookup for UI components (must come AFTER operatorLookup)
  const operators: any[] = useMemo(() => {
    const operatorArray: any[] = [];
    operatorLookup.forEach((name, id) => {
      operatorArray.push({ id, name, username: '' });
    });
    return operatorArray.sort((a, b) => a.name.localeCompare(b.name));
  }, [operatorLookup]);

  const reassignOperatorOptions = useMemo(
    () => operators.map((operator) => ({ id: operator.id, name: operator.name })),
    [operators],
  );

  // Group assignments by store + operator combination for individual tables
  const storeOperatorGroups = useMemo(() => {
    const shouldShowAllStores = (printMode && allStores) || selectedStore === 'all';
    if (!shouldShowAllStores || taskTypeFilteredRows.length === 0) {
      return [];
    }
    const groupMap = new Map();
    taskTypeFilteredRows.forEach((r: any) => {
      if (selectedOperator !== 'all') {
        const assignmentOperatorId = r.operatorId ?? r.operator_id;
        const selectedOperatorNum = parseInt(selectedOperator);
        const selectedOperatorStr = selectedOperator.toString();
        const operatorMatches = assignmentOperatorId === selectedOperatorNum ||
          assignmentOperatorId === selectedOperatorStr ||
          assignmentOperatorId?.toString() === selectedOperatorStr;
        if (!operatorMatches) return;
      }
      // Create unique key for store + operator combination
      const storeId = r.storeId ?? r.store_id;
      const operatorId = r.operatorId ?? r.operator_id;
      const groupKey = `${storeId}-${operatorId}`;

      if (!groupMap.has(groupKey)) {
        // Try to get the real operator name from multiple sources:
        // 1. Nested data structure (assignment.operator.name)
        // 2. Operator lookup map (created from API data)
        // 3. Fallback to original operatorName
        const fromAssignment = (r as any)?.assignment?.operator?.name;
        const fromLookup = operatorLookup.get(operatorId);
        const fromRow = r.operatorName;
        const betterOperatorName = fromAssignment || fromLookup || fromRow || 'Unknown Operator';

        console.log('🔍 GROUP_MAPPING: Operator ID', operatorId, '- Assignment:', fromAssignment, 'Lookup:', fromLookup, 'Row:', fromRow, 'Final:', betterOperatorName);

        groupMap.set(groupKey, {
          storeId,
          operatorId,
          storeName: r.storeName ?? 'Unknown Store',
          operatorName: betterOperatorName,
          assignments: [] as any[]
        });
      }
      groupMap.get(groupKey).assignments.push(r);
    });
    // Deduplicate within each store/operator: one row per campaign+task+format
    const result = Array.from(groupMap.values()).map((g: any) => {
      const aggMap = new Map<string, any>();
      for (const a of g.assignments as any[]) {
        // For removals, realAssignmentId is the removal_obligation_id (unique per format row).
        // For postings, fall back to campaign+task+format to deduplicate snapshot duplicates.
        const key = a.realAssignmentId != null
          ? `real-${a.realAssignmentId}`
          : `${a.campaignId ?? a.campaign_id}-${a.taskId ?? a.task_id}-${a.formatId ?? a.format_id ?? a.mediaFormatId}-${a.formatName ?? a.mediaFormatName ?? ''}`;
        if (!aggMap.has(key)) {
          aggMap.set(key, {
            ...a,
            expectedQuantity: Number(a.expectedQuantity ?? a.quantity ?? 0),
          });
        } else {
          const cur = aggMap.get(key);
          const add = Number(a.expectedQuantity ?? a.quantity ?? 0) || 0;
          cur.expectedQuantity = (Number(cur.expectedQuantity || 0) + add);
        }
      }
      const sortedAssignments = Array.from(aggMap.values()).sort((a: any, b: any) => {
        const campaignA = String(a.campaignName ?? '').localeCompare(String(b.campaignName ?? ''));
        if (campaignA !== 0) return campaignA;

        const taskA = String(a.taskName ?? '').localeCompare(String(b.taskName ?? ''));
        if (taskA !== 0) return taskA;

        const formatA = String(a.mediaFormatName ?? a.formatName ?? '').localeCompare(
          String(b.mediaFormatName ?? b.formatName ?? '')
        );
        if (formatA !== 0) return formatA;

        return String(a.realAssignmentId ?? a.id ?? '').localeCompare(
          String(b.realAssignmentId ?? b.id ?? '')
        );
      });

      return { ...g, assignments: sortedAssignments };
    });
    return result;
  }, [taskTypeFilteredRows, selectedOperator, selectedStore, printMode, allStores, operatorLookup]);

  const removalOperatorMismatches = useMemo(
    () => detectRemovalOperatorMismatches(rows as Parameters<typeof detectRemovalOperatorMismatches>[0]),
    [rows],
  );

  // Group storeOperatorGroups by operator for per-operator accordion
  const operatorGroups = useMemo(() => {
    const map = new Map<string | number, { operatorId: string | number; operatorName: string; groups: any[] }>();
    for (const g of storeOperatorGroups as any[]) {
      const key = g.operatorId;
      if (!map.has(key)) {
        map.set(key, { operatorId: g.operatorId, operatorName: g.operatorName, groups: [] });
      }
      map.get(key)!.groups.push(g);
    }
    const result = Array.from(map.values());

    // Log input counts and add invariants
    console.log('SIGNOFF_INPUT_COUNTS', {
      operatorsCount: result?.length ?? null
    });
    invariant(Array.isArray(result), 'operators not array');

    return result;
  }, [storeOperatorGroups]);

  // Bulk mobile dispatch selection state
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<Set<string>>(new Set());
  const [sendingChecked, setSendingChecked] = useState<boolean>(false);
  const [sendCheckedProgress, setSendCheckedProgress] = useState<string>('');
  const [showSendCheckedConfirm, setShowSendCheckedConfirm] = useState<boolean>(false);
  const [reassignedStore, setReassignedStore] = useState<StoreReassignedResult | null>(null);
  const [includeBriefingForChecked, setIncludeBriefingForChecked] = useState<boolean>(false);
  // Per-operator "Include briefing" selection, lifted from OperatorAccordion so
  // the tick survives the accordion remounting after a parent refetch.
  const [includeBriefingByOperator, setIncludeBriefingByOperator] = useState<Record<string, boolean>>({});
  const setIncludeBriefingForOperator = useCallback((operatorId: string, include: boolean) => {
    setIncludeBriefingByOperator((previous) => {
      if ((previous[operatorId] ?? false) === include) return previous;
      const next = { ...previous };
      if (include) {
        next[operatorId] = true;
      } else {
        delete next[operatorId];
      }
      return next;
    });
  }, []);

  const visibleOperatorIds = useMemo(
    () => operatorGroups.map((group) => String(group.operatorId)),
    [operatorGroups]
  );

  // Keep selection in sync with currently visible operators
  useEffect(() => {
    setSelectedOperatorIds((previous) => {
      const visible = new Set(visibleOperatorIds);
      const next = new Set(Array.from(previous).filter((id) => visible.has(id)));
      const unchanged = next.size === previous.size && Array.from(next).every((id) => previous.has(id));
      return unchanged ? previous : next;
    });
  }, [visibleOperatorIds]);

  const checkedOperatorCount = selectedOperatorIds.size;
  const allVisibleSelected = visibleOperatorIds.length > 0 && visibleOperatorIds.every((id) => selectedOperatorIds.has(id));

  const checkedTaskCount = useMemo(() => {
    if (!selectedCycle || selectedOperatorIds.size === 0) return 0;
    let total = 0;
    for (const group of operatorGroups) {
      const operatorId = String(group.operatorId);
      if (!selectedOperatorIds.has(operatorId)) continue;
      const tasks = buildOperatorTasksFromProcessedRows(rows, operatorId, group.operatorName, selectedCycle);
      total += tasks.items.length;
    }
    return total;
  }, [operatorGroups, rows, selectedCycle, selectedOperatorIds]);

  const handleToggleOperatorSelection = (operatorId: string, selected: boolean) => {
    setSelectedOperatorIds((previous) => {
      const next = new Set(previous);
      if (selected) {
        next.add(operatorId);
      } else {
        next.delete(operatorId);
      }
      return next;
    });
  };

  const handleSelectAllOperators = (selected: boolean) => {
    if (selected) {
      setSelectedOperatorIds(new Set(visibleOperatorIds));
      return;
    }
    setSelectedOperatorIds(new Set());
  };

  const handleSendCheckedOperators = async () => {
    if (!selectedCycle || selectedOperatorIds.size === 0) {
      return;
    }

    const operatorsToSend = operatorGroups.filter((group) => selectedOperatorIds.has(String(group.operatorId)));
    if (operatorsToSend.length === 0) {
      return;
    }

    setSendingChecked(true);
    setSendCheckedProgress('');

    let sentCount = 0;
    let skippedCount = 0;
    const failedOperatorIds: string[] = [];

    try {
      for (let index = 0; index < operatorsToSend.length; index++) {
        const operator = operatorsToSend[index];
        const operatorId = String(operator.operatorId);
        setSendCheckedProgress(`Sending ${index + 1}/${operatorsToSend.length}: ${operator.operatorName}`);

        try {
          const tasks = buildOperatorTasksFromProcessedRows(rows, operatorId, operator.operatorName, selectedCycle);
          if (tasks.items.length === 0) {
            skippedCount += 1;
            continue;
          }
          await sendOperatorTasks(tasks, {
            includeBriefing: briefingAvailableForMobileSend && includeBriefingForChecked,
          });
          sentCount += 1;
        } catch (error) {
          console.error('Failed to send checked operator', { operatorId, error });
          failedOperatorIds.push(operatorId);
        }
      }

      if (resolvedCycleId) {
        invalidateTaskDistributionQueries(queryClient, resolvedCycleId);
      }

      if (failedOperatorIds.length > 0) {
        setSelectedOperatorIds(new Set(failedOperatorIds));
        toast({
          title: 'Partial Send Complete',
          description: `Sent ${sentCount}, skipped ${skippedCount}, failed ${failedOperatorIds.length}. Failed operators remain checked.`,
          variant: 'destructive',
          duration: 6000,
        });
      } else {
        setSelectedOperatorIds(new Set());
        toast({
          title: '✅ Checked Operators Sent',
          description: `Sent ${sentCount} operator${sentCount === 1 ? '' : 's'}${skippedCount ? `, skipped ${skippedCount} with no tasks` : ''}.`,
          duration: 4000,
        });
      }
    } finally {
      setSendingChecked(false);
      setSendCheckedProgress('');
    }
  };

  // Operator accordion controls
  const operatorAccordionsRefs = useRef<Array<HTMLDetailsElement | null>>([]);
  const [expandAllVersion, setExpandAllVersion] = useState<number>(0);
  const handleExpandAll = () => {
    operatorAccordionsRefs.current.forEach((el) => {
      if (el && !el.open) el.open = true;
    });
    setExpandAllVersion((v) => v + 1);
  };
  const handleCollapseAll = () => {
    operatorAccordionsRefs.current.forEach((el) => {
      if (el && el.open) el.open = false;
    });
    setExpandAllVersion((v) => v + 1);
  };

  // PDF generation is now handled via URL parameters to avoid size limits



  // Debug: log button state on every render
  console.log("[BUTTON_STATE] selectedRetailer:", selectedRetailer, "selectedCycle:", selectedCycle, "lockedCycle:", !!effectiveLockedCycle, "disabled:", !selectedRetailer || !selectedCycle);



  // Get selected retailer and cycle data for task management panel
  const selectedRetailerData = retailers.find(r => r.id.toString() === selectedRetailer);
  const selectedCycleData = filteredBookingCycles.find(c => c.id.toString() === selectedCycle);
  const isCycleLocked = selectedCycleData && lockedCycle !== null;

  // Filter assignments based on selections AFTER fetching campaign data
  // CRITICAL FIX: Use unifiedAssignments which has the correct nested assignment structure
  const assignments = useMemo(() => {
    if (nofilter) return filterAssignmentsByTaskType(rows as any[], taskTypeFilter);
    let filtered = filterAssignmentsByTaskType(rows as any[], taskTypeFilter);
    if (selectedOperator !== 'all') {
      const sel = selectedOperator.toString();
      filtered = filtered.filter(r => (r.operatorId ?? '').toString() === sel);
    }
    if (selectedStore !== 'all') {
      filtered = filtered.filter(r => (r.storeId ?? '').toString() === selectedStore);
    }
    if (!includeUnassignedCampaigns) {
      filtered = filtered.filter(r => (r.campaignName ?? 'Unassigned') !== 'Unassigned');
    }
    return filtered;
  }, [rows, selectedOperator, selectedStore, includeUnassignedCampaigns, nofilter, taskTypeFilter]);

  // Displayed count for banner
  const displayedRowsCount = useMemo(() => {
    if (nofilter) return allRows.length;
    if (selectedStore === 'all') {
      return snapDisplayedRows.length;
    }
    return assignments.length;
  }, [nofilter, allRows, snapDisplayedRows, selectedStore, assignments]);

  const resetFilters = () => {
    setSelectedOperator('all');
    setSelectedStore('all');
    setSearch('');
    setFormatFilter('ALL');
    setIncludeUnassignedCampaigns(true);
    setTaskTypeFilter('all');
  };

  const toggleBypass = () => {
    const url = new URL(window.location.href);
    if (nofilter) {
      url.searchParams.delete('nofilter');
    } else {
      url.searchParams.set('nofilter', '1');
    }
    window.location.href = url.toString();
  };


  // Get unique assignment IDs for format resolution (assignments use assignmentId, not taskId)
  const uniqueTaskIds = useMemo(() => {
    dbg.log('[DEBUG] Sample assignment object:', assignments[0]);
    dbg.log('[DEBUG] All assignment IDs:', assignments.map((a: Assignment) => ({ assignmentId: a.assignmentId, id: a.id })));
    return [...new Set(assignments.map((a: Assignment) => a.id))].filter(Boolean) as number[];
  }, [assignments]);

  // Create a stable array of unique task IDs to avoid hooks order violations
  const stableTaskIds = useRef<number[]>([]);
  if (JSON.stringify(stableTaskIds.current) !== JSON.stringify(uniqueTaskIds)) {
    stableTaskIds.current = uniqueTaskIds;
  }

  // No format resolution fetching needed - we use finalFormats from assignment data

  // Auto-trigger print dialog in print mode after assignments are loaded
  useEffect(() => {
    if (printMode && assignments && assignments.length > 0) {
      dbg.log('PDF content rendered, triggering print...');
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        window.print();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [assignments, printMode]);

  // Deduplicate media requirements helper function
  const deduplicateMediaRequirements = (mediaReqs: any[]) => {
    const seen = new Set();
    return mediaReqs.filter((req) => {
      const key = `${req.mediaFormatId}-${req.quantity}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };


  // Group assignments by campaign and task for proper rowSpan display (single store)
  // Updated to render flexible pack formats as individual rows for discrepancy tracking
  const groupedAssignments = useMemo(() => {
    const grouped: Record<string, any[]> = {};

    unifiedAssignments.forEach((assignment: Assignment) => {
      const key = `${assignment.campaignId}-${assignment.taskId}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }

      // Get actual campaign name with proper fallback hierarchy
      const campaign = campaigns.find((c: any) => c.id === assignment.campaignId);
      const campaignName = assignment?.assignment?.campaign?.name || assignment?.campaignName || campaign?.name || "Unassigned";
      const operatorName = assignment?.assignment?.operator?.name || assignment?.operatorName || "🚨 Missing Operator";

      // Log each rendered assignment group for verification
      console.log("📋 Rendering groupedAssignment for campaign:", campaignName, "operator:", operatorName);

      // Conditional mapping: use acceptableFormatGroups if mediaRequirements is empty
      const mediaFormats = assignment.mediaRequirements?.length
        ? assignment.mediaRequirements
        : assignment.acceptableFormatGroups?.flat() || [];

      const assignmentMediaFormats = mediaFormats;
      const uniqueMediaReqs = deduplicateMediaRequirements(assignmentMediaFormats);

      // Use pre-built finalFormats from assignment data (new architecture)
      console.log('[SIGN-OFF] Processing assignment', assignment.id, 'with finalFormats:', assignment.finalFormats);

      // Handle legacy flexible packs: if finalFormats is null but acceptableFormatGroups exists
      let effectiveFinalFormats = assignment.finalFormats;
      let effectiveIsFlexible = assignment.isFlexible;

      if (!effectiveFinalFormats && assignment.acceptableFormatGroups && assignment.acceptableFormatGroups.length > 0) {
        console.log('[SIGN-OFF] Legacy flexible pack detected - building finalFormats from acceptableFormatGroups');
        effectiveIsFlexible = assignment.acceptableFormatGroups.length > 1;
        effectiveFinalFormats = assignment.acceptableFormatGroups.map(group =>
          group.map(item => ({
            formatName: `Format ${item.formatId}`, // Temporary name since format details not available
            quantity: item.quantity,
            type: 'print'
          }))
        );
      }

      if (effectiveFinalFormats && effectiveFinalFormats.length > 0) {
        // Check if this is a flexible pack
        if (effectiveIsFlexible && effectiveFinalFormats.length > 1) {
          // Add advisory note for flexible packs
          grouped[key].push({
            assignment,
            campaignName,
            mediaFormat: '⚠️ This campaign uses flexible format groups. Operators should select one full group per store.',
            expected: '',
            isFlexiblePack: true,
            isAdvisoryNote: true,
            rowType: 'advisory_note'
          });

          // Process each format group  
          effectiveFinalFormats.forEach((group, groupIndex) => {
            if (groupIndex > 0) {
              // Add OR separator between groups
              grouped[key].push({
                assignment,
                campaignName,
                mediaFormat: '--- OR ---',
                expected: '',
                isFlexiblePack: true,
                isOrSeparator: true,
                rowType: 'or_separator'
              });
            }

            // Add each format in the group
            group.forEach(format => {
              grouped[key].push({
                assignment,
                campaignName,
                mediaFormat: format.formatName,
                expected: format.quantity.toString(),
                isFlexiblePack: true,
                isFormatRow: true,
                groupIndex,
                rowType: 'format_row'
              });
            });
          });
        } else {
          // Standard pack - single group
          const singleGroup = effectiveFinalFormats[0] || [];
          singleGroup.forEach(format => {
            grouped[key].push({
              assignment,
              campaignName,
              mediaFormat: format.formatName,
              expected: format.quantity.toString(),
              isFlexiblePack: false,
              rowType: 'format_row'
            });
          });
        }
      } else {
        // Fallback to assignment media requirements
        if (uniqueMediaReqs.length === 0) {
          grouped[key].push({
            assignment,
            campaignName,
            mediaFormat: 'No media requirements',
            expected: '0',
            isFlexiblePack: false,
            rowType: 'format_row'
          });
        } else {
          uniqueMediaReqs.forEach((mediaFormat: any) => {
            grouped[key].push({
              assignment,
              campaignName,
              mediaFormat: mediaFormat.name || mediaFormat.mediaFormatName || 'Unknown Format',
              expected: mediaFormat.quantity || mediaFormat.targetUnits || '0',
              isFlexiblePack: false,
              rowType: 'format_row'
            });
          });
        }
      }
    });

    return grouped;
  }, [assignments, mediaFormats, campaignData]);

  // const storeGroups = useMemo(() => { ... }); // This was the duplicate useMemo, now removed.

  // Note: Allow UI to render even without locked cycle data so users can select retailer/cycle

  return (
    <div
      className={printMode ? "p-4 print:p-0 w-full" : "container mx-auto p-6 space-y-6"}
      data-testid="signoff-v2-root"
    >
      <StoreReassignedModal result={reassignedStore} onClose={() => setReassignedStore(null)} />

      {!printMode && (
        <>
          <PageHeader
            title="Task Distribution"
            description="Review and confirm retailer, cycle, and operator data, then generate task distribution sheets and push to mobile"
          >
            {resolvedCycleId && (
              <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-4 pr-1.5 shadow-sm md:ml-auto md:self-center">
                {(pdfDataUpdatedAt ?? 0) > 0 && (
                  <span className="whitespace-nowrap text-xs text-gray-500" data-testid="signoff-data-as-of">
                    Data as of {new Date(pdfDataUpdatedAt!).toLocaleTimeString()}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={refreshDistributionData}
                  disabled={isPdfDataFetching}
                  data-testid="signoff-refresh-data"
                  className="h-7 rounded-full px-3 text-xs font-medium text-gray-700 hover:text-gray-900"
                >
                  <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isPdfDataFetching ? 'animate-spin' : ''}`} />
                  {isPdfDataFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
              </div>
            )}
          </PageHeader>

          {removalRunBlocked && !printMode && (
            <RemovalRunBlockedBanner
              failures={removalRunResolutionFailures}
              cycleNameById={removalRunCycleNameById}
            />
          )}

          {/* Moved Filter Options to the top */}
          <CollapsibleCard
            storageKey="sign-off.filter-options"
            title="Filter Options"
            description="Select a retailer and locked cycle to manage task assignments"
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="retailer">Retailer</Label>
                  <Select value={selectedRetailer} onValueChange={setSelectedRetailer}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select retailer" />
                    </SelectTrigger>
                    <SelectContent>
                      {retailers.map((retailer) => (
                        <SelectItem key={retailer.id} value={retailer.id.toString()}>
                          {retailer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="year">Year</Label>
                  <Select value={selectedYear} onValueChange={setSelectedYear} disabled={!selectedRetailer}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="cycle">Cycle</Label>
                  <Select
                    value={selectedCycle}
                    onValueChange={setSelectedCycle}
                    disabled={!selectedRetailer}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredBookingCycles.map((cycle) => (
                        <SelectItem key={cycle.id} value={cycle.id.toString()}>
                          {`Cycle ${cycle.name} (ID ${cycle.id})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="operator">Operator</Label>
                  <Select value={selectedOperator} onValueChange={setSelectedOperator}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select operator" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All operators</SelectItem>
                      {operators.length > 0 ? (
                        operators.map((operator) => (
                          <SelectItem key={operator.id} value={operator.id.toString()}>
                            {operator.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>No operators available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="store">Store</Label>
                  <Select value={selectedStore} onValueChange={setSelectedStore}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select store" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All stores</SelectItem>
                      {filteredStores.map((store) => (
                        <SelectItem key={store.id} value={store.id.toString()}>
                          {store.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CollapsibleCard>

          {/* Legacy Task Management panel removed - now using snapshot-driven task sending */}

          {/* Cycle Lock Status */}
          {selectedCycle && (
            <CollapsibleCard
              storageKey="sign-off.cycle-lock-status"
              title={
                <span className="flex items-center gap-2">
                  {isLoadingLocked ? (
                    <>
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      Checking lock status...
                    </>
                  ) : effectiveLockedCycle ? (
                    <>
                      <Lock className="h-5 w-5 text-red-600" />
                      Cycle is locked
                    </>
                  ) : (
                    <>
                      <Unlock className="h-5 w-5 text-green-600" />
                      Cycle is not locked
                    </>
                  )}
                </span>
              }
            >
              {!isLoadingLocked && (
                <>
                    {effectiveLockedCycle ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left: Lock Status Message */}
                        <div className="space-y-4">
                          {/* Reassignment Banner - Admin only */}
                          {effectiveLockedCycle.lockReason === 'REASSIGNMENT' && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-amber-800 font-medium text-sm">
                                Cycle regenerated due to reassignment
                              </p>
                              <p className="text-amber-700 text-xs mt-1">
                                Locked on{' '}
                                {effectiveLockedCycle.lockedAt 
                                  ? new Date(effectiveLockedCycle.lockedAt).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : '—'}{' '}
                                {effectiveLockedCycle.lockedByName ? `by ${effectiveLockedCycle.lockedByName}` : ''}
                              </p>
                            </div>
                          )}
                          <div className="space-y-2">
                            <p className="text-red-600">
                              This cycle was locked{effectiveLockedCycle?.lockedByName ? ` by ${effectiveLockedCycle.lockedByName}` : ''}{' '}
                              {(() => {
                                const lockedAt = effectiveLockedCycle?.lockedAt;
                                if (!lockedAt) return "";
                                try {
                                  return `on ${new Date(lockedAt).toLocaleString()}`;
                                } catch {
                                  return "";
                                }
                              })()}.
                            </p>
                            <p className="text-sm text-gray-600">
                              Showing {snapRawRows} assignments from frozen data.
                            </p>
                          </div>
                        </div>

                        {/* Right: Selected Cycle Details */}
                        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-4">Selected cycle details</h3>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-gray-600 font-medium">Cycle Name</p>
                              <p className="text-sm text-gray-900">
                                {selectedCycleData?.name || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-medium">Retailer Name</p>
                              <p className="text-sm text-gray-900">
                                {selectedRetailerData?.name || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-medium">Start Date</p>
                              <p className="text-sm text-gray-900">
                                {selectedCycleData?.startDate 
                                  ? new Date(selectedCycleData.startDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })
                                  : '—'
                                }
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-medium">End Date</p>
                              <p className="text-sm text-gray-900">
                                {selectedCycleData?.endDate 
                                  ? new Date(selectedCycleData.endDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })
                                  : '—'
                                }
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-medium">Operator Briefing</p>
                              <p className="text-sm text-gray-900">
                                {briefingData?.source === 'cycle' && briefingData.briefing.updatedAt
                                  ? `Saved ${new Date(briefingData.briefing.updatedAt).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                    })}`
                                  : briefingData?.source === 'standards_default'
                                  ? 'Not yet saved for this cycle'
                                  : '—'}
                              </p>
                            </div>
                            <SelectedCycleLockDetails lock={effectiveLockedCycle} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-orange-600 font-medium">This cycle is not locked.</p>
                        <p className="text-sm text-gray-600">Locked cycles ensure data integrity by creating frozen snapshots of assignments. You can still view cycle data and see this warning.</p>
                      </div>
                    )}
                  </>
                )}
            </CollapsibleCard>
          )}

          {/* REMOVED: Conditional AdminTaskManagementPanel - Now rendered unconditionally at top */}

          {/* Generate PDF Button */}
          {(
            <div className="mb-3 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1 flex items-center gap-2">
              <span>
                Rows: {rows.length} / Raw: {rows.length} • Stores: {snapStores.length} • Source: {(meta?.source === 'snapshot-override') ? 'snapshot' : (meta?.source ?? 'snapshot')} • Cycle: {resolvedCycleId ?? '—'}
              </span>
              <button className="ml-auto underline" onClick={toggleBypass}>{snapNoFilter ? 'Disable bypass' : 'Bypass filters'}</button>
              <button className="underline" onClick={resetFilters}>Reset filters</button>
            </div>
          )}

          {/* Compatibility: use flat rows instead of legacy snapState */}
          {rows.length === 0 && selectedCycle && (
            <Card>
              <CardContent className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">⚠️ No assignments available for this cycle.</p>
                <p className="text-sm text-gray-500 mt-2">Check that campaigns are confirmed and have valid pack snapshots.</p>
              </CardContent>
            </Card>
          )}
          {lockedCycle && (
            <Card>
              <CardHeader>
                <CardTitle>Paper Dispatch</CardTitle>
                <CardDescription>
                  Generate export files from the locked cycle snapshot.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Main PDF generator for admins/operators */}
                <PreviewLauncher
                  cycleId={resolvedCycleId ? parseInt(resolvedCycleId) : 0}
                  cycleName={selectedCycleData?.name}
                  disabled={!selectedRetailer || !selectedCycle || !lockedCycle}
                  showExcelExports={canDownloadAdminExports}
                />

                {/* Admin-only: server rejects operators with 403 on these Excel routes */}
                {canDownloadAdminExports && (
                  <>
                    <Button
                      onClick={async () => {
                        if (!resolvedCycleId) return;
                        try {
                          await downloadAuthenticatedFile(
                            `/api/cycles/${resolvedCycleId}/kitting-excel`,
                            `Cycle_Kitting_Workbook_${resolvedCycleId}.xlsx`,
                          );
                        } catch (err) {
                          const message =
                            err instanceof AuthenticatedDownloadError
                              ? err.message
                              : err instanceof Error
                                ? err.message
                                : 'Failed to download kitting workbook';
                          toast({
                            title: 'Kitting download failed',
                            description: message,
                            variant: 'destructive',
                          });
                        }
                      }}
                      variant="outline"
                      className="w-full"
                      disabled={!selectedRetailer || !selectedCycle || !lockedCycle}
                    >
                      <FileDown className="h-4 w-4 mr-2" />
                      Kitting workbook by operator
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!resolvedCycleId) return;
                        try {
                          await downloadAuthenticatedFile(
                            `/api/cycles/${resolvedCycleId}/kitting-excel-by-store`,
                            `Cycle_Kitting_Workbook_By_Store_${resolvedCycleId}.xlsx`,
                          );
                        } catch (err) {
                          const message =
                            err instanceof AuthenticatedDownloadError
                              ? err.message
                              : err instanceof Error
                                ? err.message
                                : 'Failed to download kitting workbook';
                          toast({
                            title: 'Kitting download failed',
                            description: message,
                            variant: 'destructive',
                          });
                        }
                      }}
                      variant="outline"
                      className="w-full"
                      disabled={!selectedRetailer || !selectedCycle || !lockedCycle}
                    >
                      <FileDown className="h-4 w-4 mr-2" />
                      Kitting workbook by store
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!resolvedCycleId) return;
                        try {
                          await downloadAuthenticatedFile(
                            `/api/cycles/${resolvedCycleId}/kitting-excel-by-store?includeRemovals=true`,
                            `Cycle_Kitting_Workbook_By_Store_With_Removals_${resolvedCycleId}.xlsx`,
                          );
                        } catch (err) {
                          const message =
                            err instanceof AuthenticatedDownloadError
                              ? err.message
                              : err instanceof Error
                                ? err.message
                                : 'Failed to download kitting workbook';
                          toast({
                            title: 'Kitting download failed',
                            description: message,
                            variant: 'destructive',
                          });
                        }
                      }}
                      variant="outline"
                      className="w-full"
                      disabled={!selectedRetailer || !selectedCycle || !lockedCycle}
                    >
                      <FileDown className="h-4 w-4 mr-2" />
                      Kitting workbook by store (Postings + Removals)
                    </Button>
                  </>
                )}

                {/* Development-only legacy generators */}
                {(import.meta as any)?.env?.DEV && (
                  <>
                    <div className="text-xs text-gray-500 text-center border-t pt-3">Development only:</div>
                    <Button
                      onClick={async () => {
                        try {
                          const printCSS = `
                        @media print {
                          .sidebar, .no-print { display: none !important; }
                          .print-only { display: block !important; }
                          body { margin: 0; }
                        }
                      `;
                          const style = document.createElement('style');
                          style.innerHTML = printCSS;
                          document.head.appendChild(style);
                          window.print();
                        } catch (error) {
                          console.error("Print error:", error);
                        }
                      }}
                      variant="outline"
                      className="w-full opacity-50"
                      size="sm"
                    >
                      <FileDown className="h-4 w-4 mr-2" />
                      Browser Print (Dev)
                    </Button>


                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Operator Briefing — auto-populated from retailer standards */}
          {resolvedCycleId && (
            <CollapsibleCard
              storageKey="sign-off.operator-briefing"
              title={
                <span className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Operator Briefing
                </span>
              }
              description={
                <>
                  {briefingData?.source === 'standards_default'
                    ? 'Pre-filled from retailer standards. Save to lock in for this cycle.'
                    : briefingData?.source === 'cycle'
                    ? 'Saved for this cycle. Edit fields or re-save to confirm briefing content.'
                    : 'Loading...'}
                  {briefingData?.source === 'cycle' && (
                    <span className="block mt-1 text-xs text-muted-foreground">
                      Current version saved {briefingSavedAtLabel}
                    </span>
                  )}
                  {acksData && acksData.totalAssigned > 0 && (
                    <span className="block mt-1">
                      <span className="font-medium text-foreground">
                        {acksData.acknowledgedCurrent} of {acksData.totalAssigned}
                      </span>{' '}
                      assigned operators have acknowledged the current version.
                    </span>
                  )}
                </>
              }
              headerActions={
                <div className="flex flex-col items-end gap-2">
                  <Button
                    size="sm"
                    disabled={briefingSaveMutation.isPending}
                    onClick={() =>
                      briefingSaveMutation.mutate({
                        content: briefingEdits,
                      })
                    }
                    title="Save the current briefing content for this cycle"
                  >
                    {briefingSaveMutation.isPending ? 'Saving...' : 'Save Briefing'}
                  </Button>
                </div>
              }
            >
              <div className="mb-3 grid grid-cols-1 gap-2 rounded-lg border bg-muted/30 p-3 text-xs md:grid-cols-4">
                <div>
                  <p className="font-medium text-foreground">Source</p>
                  <p className="text-muted-foreground">
                    {briefingData?.source === 'cycle' ? 'Cycle-specific version' : 'Retailer standard draft'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Current version</p>
                  <p className="text-muted-foreground">{briefingSavedAtLabel}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Mobile sections</p>
                  <p className="text-muted-foreground">
                    {briefingSectionCount} section{briefingSectionCount === 1 ? '' : 's'} will appear on mobile
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Acknowledgements</p>
                  <p className="text-muted-foreground">
                    {acksData
                      ? acksData.totalAssigned === 0
                        ? 'No operators assigned yet'
                        : `${acksData.acknowledgedCurrent} of ${acksData.totalAssigned} current`
                      : 'Loading...'}
                  </p>
                </div>
              </div>
              {briefingDirty && (
                <p className="mb-3 rounded border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs text-blue-800">
                  You have unsaved briefing edits. Mobile operators will continue to see the current saved version until you save.
                </p>
              )}
              {briefingData?.newerStandardsAvailable && !refreshedStandardsVersionId && (
                <div className="mb-3 flex items-center justify-between rounded border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-xs text-amber-800">
                    Retailer standards have been updated since this briefing was last saved.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-3 shrink-0 text-xs"
                    onClick={handleRefreshFromStandards}
                  >
                    Pull latest standards
                  </Button>
                </div>
              )}
              {briefingLoading ? (
                <p className="text-sm text-muted-foreground">Loading briefing...</p>
              ) : (
                <div className="flex gap-6">
                  {/* Left: editor fields */}
                  <div className="flex-1 space-y-4 min-w-0">
                      <div>
                        <Label className="text-sm font-medium">
                          Cycle-Specific Message
                          <span className="ml-2 font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5 mb-1">
                          Add a one-off note for this cycle only — e.g. "Cold weather expected, bring heatguns." Leave blank if not needed.
                        </p>
                        <BriefingRichTextEditor
                          content={briefingEdits.customMessage ?? ''}
                          onChange={(html) => handleBriefingFieldChange('customMessage', html)}
                          placeholder="Optional cycle-specific note"
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium">Installation Standards</Label>
                          <BriefingRichTextEditor
                            content={briefingEdits.installationStandards ?? ''}
                            onChange={(html) => handleBriefingFieldChange('installationStandards', html)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Removal Standards</Label>
                          <BriefingRichTextEditor
                            content={briefingEdits.removalStandards ?? ''}
                            onChange={(html) => handleBriefingFieldChange('removalStandards', html)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Conduct in Store</Label>
                          <BriefingRichTextEditor
                            content={briefingEdits.conductInStore ?? ''}
                            onChange={(html) => handleBriefingFieldChange('conductInStore', html)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Submission Requirements</Label>
                          <BriefingRichTextEditor
                            content={briefingEdits.submissionRequirements ?? ''}
                            onChange={(html) => handleBriefingFieldChange('submissionRequirements', html)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>

                  {/* Right: sticky phone preview */}
                  <div className="hidden lg:block sticky top-4 self-start">
                    <BriefingMobilePreview edits={briefingEdits} />
                  </div>
                </div>
              )}
            </CollapsibleCard>
          )}

          {/* Briefing acknowledgements — operator audit trail */}
          {resolvedCycleId && acksData && (
            <BriefingAcknowledgementsCard data={acksData} />
          )}
        </>
      )}



      {/* Single Store Table - now renders flat rows for the selected store */}
      {lockedCycle && displayedRowsCount > 0 && selectedStore !== 'all' && (
        <div className="store-section">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {!printMode && <Package className="h-5 w-5" />}
                {stores.find(s => s.id === parseInt(selectedStore))?.name || 'Single Store'}
                {(() => {
                  if (selectedOperator !== 'all') {
                    // Show selected operator name
                    return (
                      <span className="text-sm font-normal text-blue-600">
                        - {operators.find(o => o.id === parseInt(selectedOperator))?.name || operators.find(o => o.id === parseInt(selectedOperator))?.username || 'Operator'}
                      </span>
                    );
                  } else {
                    // Use locked summary data for single store view
                    let operatorNames: string[] = [];

                    if (lockedSummary && selectedStore !== 'all') {
                      // Use the locked summary data (strictly from locked_assignments)
                      operatorNames = lockedSummary.storeOperators.map((op: any) => op.name);

                      if (process.env.NODE_ENV !== 'production') {
                        console.log(`🔍 [FRONTEND] Single store using locked summary:`, operatorNames);
                      }
                    } else {
                      // Fallback: build from assignments with filtering (legacy support)
                      const uniqueOperatorNames = Array.from(new Set(
                        assignments
                          .filter((a: any) => {
                            // Only include valid operator IDs and filter out Admin users
                            const operatorId = a.operatorId;
                            const operatorName = (a as any)?.assignment?.operator?.name || a?.operatorName;
                            return operatorId &&
                              typeof operatorId === 'number' &&
                              operatorName &&
                              !operatorName.includes('Admin');
                          })
                          .map((a: any) => (
                            (a as any)?.assignment?.operator?.name || a?.operatorName || 'Unknown Operator'
                          ))
                      )).sort() as string[]; // Sort alphabetically for consistent ordering

                      operatorNames = uniqueOperatorNames;

                      if (process.env.NODE_ENV !== 'production') {
                        console.log(`🔍 [FRONTEND] Single store fallback to assignments:`, operatorNames);
                      }
                    }

                    // Show neutral message if no operators assigned
                    if (operatorNames.length === 0) {
                      return (
                        <span className="text-sm font-normal text-gray-500">
                          No locked operators
                        </span>
                      );
                    }

                    return (
                      <span className={`text-sm font-normal ${operatorNames.some((name: string) => (name || '').includes('🚨')) ? 'text-red-600' : 'text-blue-600'}`}>
                        - {operatorNames.join(', ')}
                      </span>
                    );
                  }
                })()}
                {!printMode && (
                  <span className="text-sm font-normal text-gray-500">
                    {assignments.length} assignments
                  </span>
                )}
              </CardTitle>
              {!printMode && (
                <CardDescription>
                  Frozen assignment data from locked cycle
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <AssignmentTable assignments={unifiedAssignments as ApiAssignment[]} showRemovalBasis />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Deleted Campaigns Warning */}
      {selectedCycle && lockedCycle && assignments.length > 0 && !printMode && (() => {
        const deletedCampaigns = assignments.filter((a: any) => a.removedAfterLock);
        if (deletedCampaigns.length > 0) {
          return (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <p className="text-orange-800 text-sm">
                    <strong>Admin Notice:</strong> This cycle contains {deletedCampaigns.length} assignments from campaigns that were deleted after the cycle was locked.
                    These assignments are displayed as "[Deleted After Lock]" in gray.
                    This is normal behavior for maintaining data integrity in locked cycles.
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        }
        return null;
      })()}

      {/* Individual tables for each store/operator combination */}
      {storeOperatorGroups.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Mobile Dispatch</CardTitle>
            <CardDescription>
              Review all filtered operators below. Use expand/collapse to inspect assignment-level details before sending.
            </CardDescription>
          </CardHeader>
          <CardContent className={printMode ? "space-y-6 print-store-tables" : "space-y-4"}>
            {!printMode && (() => {
              const gaps = unifiedPdfData?.data?.removalGaps ?? [];
              return gaps.length > 0 && resolvedCycleId ? (
                <RemovalCoverageGaps gaps={gaps} cycleId={resolvedCycleId} />
              ) : null;
            })()}
            {!printMode && removalOperatorMismatches.length > 0 && (
              <RemovalOperatorMismatchBanner mismatches={removalOperatorMismatches} />
            )}
            {!printMode ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <ToggleGroup
                    type="single"
                    value={taskTypeFilter}
                    onValueChange={(value) => {
                      if (value === 'all' || value === 'POSTING' || value === 'REMOVAL') {
                        setTaskTypeFilter(value);
                      }
                    }}
                    className="rounded-md border bg-muted/40 p-1"
                    data-testid="task-type-filter"
                  >
                    <ToggleGroupItem value="all" aria-label="All tasks" className="px-3 text-sm">
                      All ({taskTypeCounts.all})
                    </ToggleGroupItem>
                    <ToggleGroupItem value="POSTING" aria-label="Postings only" className="px-3 text-sm">
                      Postings ({taskTypeCounts.postingCount})
                    </ToggleGroupItem>
                    <ToggleGroupItem value="REMOVAL" aria-label="Removals only" className="px-3 text-sm">
                      Removals ({taskTypeCounts.removalCount})
                    </ToggleGroupItem>
                  </ToggleGroup>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="expand-all"
                      className="text-sm text-blue-600 underline"
                      onClick={handleExpandAll}
                    >
                      Expand all
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      data-testid="collapse-all"
                      className="text-sm text-blue-600 underline"
                      onClick={handleCollapseAll}
                    >
                      Collapse all
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-gray-700">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(checked) => handleSelectAllOperators(checked === true)}
                      aria-label="Select all visible operators"
                    />
                    <span>Select all ({operatorGroups.length})</span>
                  </label>
                  <label
                    className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${
                      briefingAvailableForMobileSend ? 'text-gray-700' : 'bg-gray-50 text-gray-500'
                    }`}
                    title={
                      briefingAvailableForMobileSend
                        ? 'Include the saved cycle briefing when sending checked operators'
                        : 'Save a cycle briefing first to enable briefing delivery'
                    }
                  >
                    <Checkbox
                      checked={includeBriefingForChecked}
                      disabled={!briefingAvailableForMobileSend || sendingChecked}
                      onCheckedChange={(checked) => setIncludeBriefingForChecked(checked === true)}
                      aria-label="Include briefing when sending checked operators"
                    />
                    <span>Include briefing</span>
                  </label>
                  <Button
                    type="button"
                    data-testid="send-checked-operators"
                    onClick={() => setShowSendCheckedConfirm(true)}
                    disabled={sendingChecked || checkedOperatorCount === 0}
                  >
                    {sendingChecked ? (sendCheckedProgress || 'Sending...') : `Send Checked Operators (${checkedOperatorCount})`}
                  </Button>
                </div>
              </div>
            ) : null}

            {operatorGroups.map((opIndexItem: { operatorId: number | string; operatorName: string; groups: any[] }, opIndex: number) => {
              // Trace each operator section render (one-time per operator)
              traceRender.current('operator-section', opIndexItem.operatorId);

              const resolvedOperatorName = (() => {
                const lookupName = operatorLookup.get(Number(opIndexItem.operatorId));
                const originalName = String(opIndexItem.operatorName);
                const finalName = lookupName || originalName;
                console.log('🔍 ACCORDION_NAME: ID', opIndexItem.operatorId, 'Lookup:', lookupName, 'Original:', originalName, 'Final:', finalName);
                return finalName;
              })();

              const operatorIdValue = String(opIndexItem.operatorId);

              return (
                <OperatorAccordion
                  key={operatorIdValue}
                  operatorId={operatorIdValue}
                  operatorName={resolvedOperatorName}
                  storeCount={opIndexItem.groups.length}
                  defaultOpen={false}
                  detailsRef={(el) => { operatorAccordionsRefs.current[opIndex] = el; }}
                  cycleId={selectedCycle}
                  snapshot={rows}
                  initialMobileSentAt={mobileSentLookup.get(operatorIdValue) ?? null}
                  initialPdfSentAt={pdfSentLookup.get(operatorIdValue) ?? null}
                  briefingAvailable={briefingAvailableForMobileSend}
                  includeBriefing={!!includeBriefingByOperator[operatorIdValue]}
                  onIncludeBriefingChange={(include) => setIncludeBriefingForOperator(operatorIdValue, include)}
                  selected={selectedOperatorIds.has(operatorIdValue)}
                  onSelectedChange={(selected) => handleToggleOperatorSelection(operatorIdValue, selected)}
                  onSendToMobile={() => {
                    if (resolvedCycleId) {
                      invalidateTaskDistributionQueries(queryClient, resolvedCycleId);
                    }
                  }}
                  onPreview={() => openOperatorPreview(operatorIdValue, resolvedOperatorName, opIndexItem.groups)}
                >
                  <div className="space-y-4">
                    {opIndexItem.groups.map((group: any) => {
                      const storeMismatches = mismatchesForStoreOperator(
                        Number(group.storeId),
                        Number(group.operatorId),
                        removalOperatorMismatches,
                      );
                      return (
                      <StoreAssignmentCard
                        key={`${group.storeId}-${group.operatorId}`}
                        storeId={group.storeId}
                        storeName={group.storeName}
                        operatorId={group.operatorId}
                        operatorName={group.operatorName}
                        cycleId={resolvedCycleId || selectedCycle || ''}
                        assignmentCount={group.assignments.length}
                        operatorMismatches={storeMismatches}
                        printMode={printMode}
                        isSent={storeSentLookup.get(`${group.operatorId}-${group.storeId}`) ?? false}
                        reassignOperators={reassignOperatorOptions}
                        onReassigned={setReassignedStore}
                        onRefresh={() => {
                          if (resolvedCycleId) {
                            invalidateTaskDistributionQueries(queryClient, resolvedCycleId);
                          }
                        }}
                      >
                        <AssignmentTable assignments={group.assignments as ApiAssignment[]} showRemovalBasis />
                      </StoreAssignmentCard>
                      );
                    })}
                  </div>
                </OperatorAccordion>
              );
            })}

            {!printMode && (
              <DistributionConfirmModal
                open={showSendCheckedConfirm}
                onOpenChange={setShowSendCheckedConfirm}
                method="mobile"
                operatorName={`${checkedOperatorCount} checked operators`}
                taskCount={checkedTaskCount}
                includeBriefing={briefingAvailableForMobileSend && includeBriefingForChecked}
                briefingAvailable={briefingAvailableForMobileSend}
                onConfirm={handleSendCheckedOperators}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Check for render mismatch after operators are rendered */}
      {!printMode && (() => {
        const operatorsCount = operatorGroups?.length ?? 0;
        if (operatorsCount > 0) {
          setTimeout(() => {
            const accordionCount = document.querySelectorAll('[data-testid="operator-accordion"]').length;
            if (accordionCount === 0) {
              console.error('UI_RENDER_MISMATCH_NO_ACCORDIONS');
            }
          }, 100);
        }
        return null;
      })()}

      {/* Loading state */}
      {(isLoadingAssignments || isTransientAssignmentRetry) && (
        <Card>
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-12 w-12 mx-auto mb-4 text-gray-400 animate-spin" />
            <p className="text-gray-600">
              {isTransientAssignmentRetry ? 'System is busy — retrying…' : 'Loading assignments...'}
            </p>
          </CardContent>
        </Card>
      )}

      {isTransientAssignmentFailure && (
        <TransientLoadNotice
          message="The database connection pool is under pressure. Automatic retries have paused — try again in a moment."
          onRetry={retryAssignmentLoad}
          isRetrying={isBootstrapFetching || isLockedLegacyFetching || isPdfDataFetchingLegacy}
        />
      )}

      {/* Error state */}
      {assignmentError && (
        <Card>
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
            <p className="text-red-600">Failed to load assignments</p>
          </CardContent>
        </Card>
      )}

      {/* Debug info when no assignments */}
      {!isLoadingAssignments && !assignmentError && selectedCycle && lockedCycle && rawRows === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No assignments found for this cycle.</p>
            <div className="mt-4 text-xs text-gray-500">
              <p>Debug info:</p>
              <p>Cycle ID: {resolvedCycleId}</p>
              <p>Assignment Rows Present: {rows && Array.isArray(rows) && rows.length > 0 ? 'Yes' : 'No'}</p>
              <p>Raw Data Rows: {rows?.length || 0}</p>
              <p>Meta Source: {meta?.source || 'Unknown'}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters removed all rows */}
      {!isLoadingAssignments && !assignmentError && selectedCycle && lockedCycle && rawRows > 0 && displayedRowsCount === 0 && (
        <Card>
          <CardContent className="p-4 text-center bg-yellow-50 text-yellow-800">
            Filters removed all rows. <button className="underline" onClick={resetFilters}>Reset filters</button>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!previewOperator} onOpenChange={(open) => {
        if (!open) {
          setPreviewOperator(null);
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assignment Preview
              {previewOperator && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  - {previewOperator.operatorName} ({previewOperator.assignments.length} tasks)
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {previewOperator && (
            <AssignmentTable
              assignments={previewOperator.assignments}
              showRemovalBasis
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
